/**
 * Mobile-first Phase-1 CRM Query Workspace.
 *
 * Design rules (per spec):
 *  - Mobile usability > desktop usability.
 *  - Single-column. Large touch targets (≥44px). No multi-col forms.
 *  - All optional sections start COLLAPSED. Operator taps a row to expand.
 *  - Saving requires only ONE of: requirement_notes, an attachment, or a task.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRightCircle, Bed, Bus, ChevronDown, ChevronUp, Clock, Copy, Download,
  FileText, Link2, Loader2, Mail, MessageCircle, Paperclip, Plus, Receipt, Save, Search, Share2, Tag,
  Trash2, Upload, User, Users, X, Megaphone, CheckCircle2, XCircle, UserCheck, Package, Sparkles,
} from 'lucide-react';
import { API } from '@/App';
import BulkOutreachSheet from '@/components/BulkOutreachSheet';
import { personaliseWhatsAppText } from '@/lib/outreachPersonalise';
import BrochureManager from '@/components/BrochureManager';
import { AccommodationTypeTagger, ContactTagger } from '@/components/vendors/InlineTaggers';
import DocumentsPanel from '@/components/DocumentsPanel';

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Customer-facing branded share domain. Every public link surfaced to
// vendors / customers must be anchored here so the URL never leaks the
// backend host name. Falls back to pollendots.com.
const PUBLIC_SHARE_BASE = (process.env.REACT_APP_PUBLIC_SHARE_DOMAIN || 'https://pollendots.com').replace(/\/$/, '');

const SOURCES = ['WhatsApp', 'Phone Call', 'Website', 'Instagram', 'Facebook',
  'Email', 'Referral', 'Repeat Customer', 'Walk In', 'Other'];
const MEALS = ['Veg', 'Non Veg', 'Eggitarian', 'Jain', 'Vegan', 'Mixed Group'];
const VEH_CATS = ['Sedan', 'Premium Sedan', 'Ertiga', 'Innova', 'Tempo Traveller', 'Urbania'];
const TASK_TYPES = ['Contact For Details', 'Check Hotel Availability',
  'Check Vehicle Availability', 'Create Itinerary', 'Create Quotation', 'Create Invoice'];
const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];
const TASK_STATUSES = ['Pending', 'In Progress', 'Completed'];

// Lifecycle stages + color tokens. Mirrors the list page so the status
// badge looks consistent everywhere a query is shown.
const STATUS_COLORS = {
  'Open':             'bg-amber-100 text-amber-900 border-amber-200',
  'Unassigned':       'bg-orange-100 text-orange-900 border-orange-200',
  'In Progress':      'bg-blue-100 text-blue-900 border-blue-200',
  'Itinerary Shared': 'bg-sky-100 text-sky-900 border-sky-200',
  'Quoted':           'bg-violet-100 text-violet-900 border-violet-200',
  'Invoiced':         'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200',
  'Tour Created':     'bg-emerald-100 text-emerald-900 border-emerald-200',
  'Closed':           'bg-slate-200 text-slate-700 border-slate-300',
};

// ----- Section wrapper: tap row to expand. Mobile-first ----------------------
const Section = ({ title, hint, icon: Icon, open, onToggle, children, testid }) => (
  <Card data-testid={testid} className="overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-3 p-4 text-left min-h-[56px] hover:bg-muted/30"
    >
      {Icon && <Icon className="w-5 h-5 text-amber-700 shrink-0" />}
      <span className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5 truncate">{hint}</div>}
      </span>
      {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
    </button>
    {open && <CardContent className="space-y-3 pt-0 pb-4">{children}</CardContent>}
  </Card>
);

// ----- CollapsibleBlock: inner card minimised by default --------------------
// Per product rule: every card across the system starts collapsed. Tap the
// summary header to expand. Used by HotelRow, VehicleRow, GuideRow, PackageBlock,
// TaskBlock — and any future card.
const CollapsibleBlock = ({ icon: Icon, title, subtitle, badge, onRemove, defaultOpen = false, testid, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 p-3 min-h-[48px]">
        <button type="button"
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          onClick={() => setOpen(o => !o)}
          data-testid={testid ? `${testid}-toggle` : undefined}>
          {Icon && <Icon className="w-4 h-4 text-muted-foreground shrink-0" />}
          <span className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{title || 'Untitled'}</div>
            {subtitle && <div className="text-[11px] text-muted-foreground truncate">{subtitle}</div>}
          </span>
          {badge}
          {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
        </button>
        {onRemove && (
          <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-destructive shrink-0"
            onClick={onRemove}
            data-testid={testid ? `${testid}-remove` : undefined}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
      {open && <div className="px-3 pb-3 pt-0 space-y-2 border-t">{children}</div>}
    </div>
  );
};

// ----- Itinerary day-slice helpers ------------------------------------------
// The package brief is the canonical "Day 1: X. Day 2: Y. Day 3: Z." text.
// Guide assignments need only the days they cover. Compute by date offset
// against tour.start_date.
const _dayOffset = (date, anchor) => {
  if (!date || !anchor) return null;
  const a = new Date(`${anchor}T00:00:00`);
  const d = new Date(`${date}T00:00:00`);
  if (isNaN(a.getTime()) || isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - a.getTime()) / 86400000);
};

const _parseBriefByDay = (brief) => {
  if (!brief) return {};
  const re = /Day\s*(\d+)\s*[:\-–]\s*/gi;
  const map = {};
  const text = String(brief);
  let lastDay = null; let lastIdx = 0; let m;
  while ((m = re.exec(text)) !== null) {
    if (lastDay !== null) {
      map[lastDay] = text.slice(lastIdx, m.index).trim();
    }
    lastDay = parseInt(m[1], 10);
    lastIdx = re.lastIndex;
  }
  if (lastDay !== null) map[lastDay] = text.slice(lastIdx).trim();
  return map;
};

const sliceBriefByDateRange = (brief, anchorDate, dateFrom, dateTo) => {
  const s = _dayOffset(dateFrom, anchorDate);
  const e = _dayOffset(dateTo, anchorDate);
  if (s === null || e === null || e < s) return '';
  const map = _parseBriefByDay(brief);
  const lines = [];
  for (let d = s + 1; d <= e + 1; d++) {
    if (map[d]) lines.push(`Day ${d}: ${map[d]}`);
  }
  return lines.join('\n');
};

// ----- Phone normalisation for WhatsApp wa.me links --------------------------
// wa.me requires digits only, country code first, no leading + or spaces.
// We default to India (+91) when a 10-digit local number is supplied.
const normalisePhone = (raw) => {
  if (!raw) return '';
  let p = String(raw).replace(/[^\d+]/g, '');
  if (p.startsWith('+')) p = p.slice(1);
  // Drop leading zeros (some operators store "0xxxxxxxxxx").
  p = p.replace(/^0+/, '');
  // 10-digit Indian mobile w/o country code → prepend 91.
  if (p.length === 10) p = `91${p}`;
  // wa.me accepts 11-15 digit international numbers; anything shorter is bogus.
  if (p.length < 10) return '';
  return p;
};


// ----- Customer search (typeahead) -------------------------------------------
const CustomerSearch = ({ value, onPick, onQuickName, onQuickPhone }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  // Debounced search — 200ms is generous on mobile keyboards.
  useEffect(() => {
    let live = true;
    const id = setTimeout(async () => {
      if (!q || q.length < 2) { setResults([]); return; }
      setBusy(true);
      try {
        const res = await fetch(`${API}/pollen/query-customers/search?q=${encodeURIComponent(q)}`, {
          credentials: 'include', headers: authHeaders(),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (live) { setResults(data || []); setShow(true); }
      } finally { if (live) setBusy(false); }
    }, 200);
    return () => { live = false; clearTimeout(id); };
  }, [q]);
  // Smart: if `q` is mostly digits, push as phone; else as name.
  useEffect(() => {
    if (!q) { onQuickName(''); onQuickPhone(''); return; }
    const isPhone = /^[+0-9\s]+$/.test(q.trim()) && q.replace(/\D+/g, '').length >= 7;
    if (isPhone) { onQuickPhone(q.trim()); onQuickName(''); }
    else { onQuickName(q.trim()); onQuickPhone(''); }
  }, [q, onQuickName, onQuickPhone]);
  if (value) {
    return (
      <div className="rounded-lg border p-3 bg-emerald-50 flex items-center gap-2" data-testid="customer-selected">
        <User className="w-4 h-4 text-emerald-700" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{value.name || '(no name)'}</div>
          <div className="text-xs text-muted-foreground truncate">{value.phone}</div>
        </div>
        {value.provisional && <Badge variant="outline" className="text-[10px]">Provisional</Badge>}
        <Button size="sm" variant="ghost" className="h-8" onClick={() => onPick(null)}>Change</Button>
      </div>
    );
  }
  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by name or mobile (e.g. 9876543210)"
          className="h-12 pl-10 text-base"
          data-testid="customer-search"
        />
      </div>
      {show && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full border rounded-lg bg-card shadow-lg max-h-64 overflow-y-auto">
          {results.map(c => (
            <button
              type="button"
              key={c.id}
              onClick={() => { onPick(c); setQ(''); setShow(false); }}
              className="w-full text-left p-3 hover:bg-muted flex items-center gap-2 border-b last:border-0"
              data-testid={`customer-result-${c.id}`}
            >
              <User className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{c.name || '(no name)'}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {c.phone}
                  {c.via && <span className="ml-1 italic">· {c.via}</span>}
                </div>
              </div>
              {c.provisional && <Badge variant="outline" className="text-[10px]">Provisional</Badge>}
            </button>
          ))}
        </div>
      )}
      {q && !busy && results.length === 0 && q.length >= 2 && (
        <p className="text-xs text-muted-foreground mt-2 px-1">
          No match — we'll create a provisional customer when you save.
        </p>
      )}
    </div>
  );
};

// ----- Vehicle row -----------------------------------------------------------
const VehicleRow = ({ v, onChange, onRemove, indexLabel }) => {
  const needsSeats = v.category === 'Tempo Traveller' || v.category === 'Urbania';
  const legLabel = (v.transfer_from || v.transfer_to)
    ? `${v.transfer_from || '—'} → ${v.transfer_to || '—'}`
    : '';
  return (
    <CollapsibleBlock
      icon={Bus}
      title={v.category || indexLabel || 'Vehicle'}
      subtitle={[legLabel, v.qty ? `${v.qty} ${v.qty > 1 ? 'units' : 'unit'}` : '']
        .filter(Boolean).join(' · ')}
      onRemove={onRemove}
      testid="vehicle-row">
      <div className="flex items-center gap-2">
        <Select value={v.category || ''} onValueChange={val => onChange({ ...v, category: val })}>
          <SelectTrigger className="h-11 flex-1"><SelectValue placeholder="Vehicle category" /></SelectTrigger>
          <SelectContent>
            {VEH_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Quantity</Label>
          <Input type="number" min="1" inputMode="numeric" className="h-11"
            value={v.qty ?? 1}
            onChange={e => onChange({ ...v, qty: parseInt(e.target.value || '0', 10) })} />
        </div>
        {needsSeats && (
          <div>
            <Label className="text-xs">Seat capacity</Label>
            <Input type="number" inputMode="numeric" className="h-11"
              value={v.seat_capacity || ''}
              onChange={e => onChange({ ...v, seat_capacity: parseInt(e.target.value || '0', 10) })} />
          </div>
        )}
      </div>

      {/* In-card vendor picker — Drivers/Operators matching pickup or
          drop location of this leg. */}
      <div>
        <Label className="text-xs">Drivers picked for this leg</Label>
        <VendorMultiPicker
          type="Driver"
          locations={[v.transfer_from, v.transfer_to].filter(Boolean)}
          selectedIds={v.vendor_ids || []}
          onChange={(ids) => onChange({ ...v, vendor_ids: ids })}
          testidPrefix="vehicle-vp"
        />
      </div>
    </CollapsibleBlock>
  );
};

// ----- HotelRow: one per overnight halt --------------------------------------
// Spec: accommodation type (multi-select from the 6-tier taxonomy), overnight
// location + alias chips (Cherrapunjee/Sohra, Jaigaon/Phuentsholing), dates,
// rooms, meal plan, adults, children (with per-child age inputs), room
// category, price range, special request. All optional.
const ACCOMMODATION_TYPES = ['Hotel', 'Resort', 'Villa', 'Boutique', 'Homestay', 'Luxury Chain'];
const MEAL_PLANS = ['EP', 'CP', 'MAP', 'AP', 'AI'];

// --- Date helpers for halt tiling ---------------------------------------
// Add `n` days to an ISO `YYYY-MM-DD` string. Returns '' on invalid input.
const addDays = (iso, n) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + (parseInt(n, 10) || 0));
  return d.toISOString().slice(0, 10);
};
const sumHaltNights = (halts) =>
  (halts || []).reduce((s, h) => s + (parseInt(h.nights || 0, 10) || 0), 0);
// Tile halt check-in/check-out end-to-end starting from `tourStart`. Each
// halt's check_out = check_in + nights. Operator-edited check_in on the
// first halt overrides `tourStart` for subsequent tiles.
const retileHaltDates = (halts, tourStart) => {
  let cursor = tourStart || (halts[0] && halts[0].check_in) || '';
  return (halts || []).map((h) => {
    const ci = cursor || h.check_in || '';
    const n = parseInt(h.nights || 0, 10) || 0;
    const co = ci ? addDays(ci, n) : (h.check_out || '');
    cursor = co || cursor;
    return { ...h, check_in: ci, check_out: co };
  });
};
// Resolve inherited fields for a halt: returns the values the email
// composer / outreach pre-fill should use (override values win over
// tour-level defaults).
const inheritedHaltFields = (h, tour) => {
  if (h.override) {
    const o = h.overrides || {};
    return {
      rooms: o.rooms,
      adults: o.adults,
      children: o.children,
      child_ages: o.child_ages || [],
      accommodation_types: o.accommodation_types || [],
      meal_plan: o.meal_plan || '',
    };
  }
  return {
    rooms: tour?.rooms,
    adults: tour?.adults,
    children: tour?.children,
    child_ages: tour?.child_ages || [],
    accommodation_types: tour?.accommodation_types || [],
    meal_plan: tour?.meal_plan || '',
  };
};

const ChipMulti = ({ value = [], onChange, options, testidPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map(o => {
      const active = (value || []).includes(o);
      return (
        <button key={o} type="button"
          onClick={() => onChange(active ? value.filter(x => x !== o) : [...(value || []), o])}
          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
            active ? 'bg-foreground text-background border-foreground'
                   : 'bg-white text-foreground border-border'
          }`}
          data-testid={testidPrefix ? `${testidPrefix}-${o.toLowerCase().replace(/\s+/g, '-')}` : undefined}>
          {o}
        </button>
      );
    })}
  </div>
);

// In-card vendor multi-select picker. Pulls /api/pollen/vendors once on
// mount (cached at module level so multiple cards share the same fetch),
// filters by `type` and `locations` (OR-match), shows a search + checkbox
// list. The selected vendor IDs are stored on the card object so when
// the operator clicks "Send outreach (this halt)" the IDs are pre-passed
// to the BulkOutreachSheet in compact mode.
let __vendorCachePromise = null;
const fetchVendorsOnce = () => {
  if (__vendorCachePromise) return __vendorCachePromise;
  __vendorCachePromise = (async () => {
    try {
      const r = await fetch(`${API}/pollen/vendors?limit=5000`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (!r.ok) return [];
      const data = await r.json();
      return Array.isArray(data) ? data : (data?.items || []);
    } catch { return []; }
  })();
  return __vendorCachePromise;
};

const VendorMultiPicker = ({ type, locations = [], accommodationTypes = [], selectedIds = [], onChange, testidPrefix = 'vp' }) => {
  // `type` may be a string ('Hotel') OR an array (['Agent','Transport']) —
  // package operators should match BOTH Agent + Transport because some
  // travel agents are filed as either type in the legacy data.
  const typeSet = useMemo(() => {
    const arr = Array.isArray(type) ? type : (type ? [type] : []);
    return new Set(arr.map(s => (s || '').toLowerCase()));
  }, [type]);
  const [vendors, setVendors] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { fetchVendorsOnce().then(vs => { setVendors(vs); setLoaded(true); }); }, []);

  // Filter — type match required; locations OR-match (any non-empty
  // location string is matched against vendor.location/address); for
  // hotels, accommodation_type filter is applied if provided.
  const matches = useMemo(() => {
    const t = (Array.isArray(type) ? type[0] : type || '').toLowerCase(); // for hotel-acc check
    const locs = (locations || []).map(l => (l || '').toLowerCase().trim()).filter(Boolean);
    const accSet = new Set((accommodationTypes || []).map(s => s.toLowerCase()));
    const q = (search || '').toLowerCase().trim();
    return vendors.filter(v => {
      if (typeSet.size > 0 && !typeSet.has((v.type || '').toLowerCase())) return false;
      if (locs.length) {
        const hay = `${v.location || ''} ${v.address || ''}`.toLowerCase();
        if (!locs.some(l => hay.includes(l))) return false;
      }
      if (t === 'hotel' && accSet.size > 0) {
        // Permissive accommodation filter on multi-tagged vendors. A
        // property may carry multiple types (Boutique + Resort, Homestay +
        // Boutique, …). Match if ANY of the vendor's types intersects with
        // the picked filter set. Vendors with NO types set are still
        // shown (legacy data is permissive). Singular legacy field counts.
        const vTypes = (v.accommodation_types && v.accommodation_types.length)
          ? v.accommodation_types
          : (v.accommodation_type ? [v.accommodation_type] : []);
        if (vTypes.length > 0) {
          const has = vTypes.some(x => accSet.has((x || '').toLowerCase()));
          if (!has) return false;
        }
      }
      if (q) {
        const hay = `${v.name || ''} ${v.location || ''} ${v.email || ''} ${v.phone || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [vendors, type, typeSet, locations, accommodationTypes, search]);

  const sel = new Set(selectedIds || []);
  const togglePick = (id) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };
  const pickAllVisible = () => {
    const next = new Set(sel);
    matches.forEach(v => next.add(v.id));
    onChange([...next]);
  };
  const clearAll = () => onChange([]);

  const selectedVendors = vendors.filter(v => sel.has(v.id));

  return (
    <div className="rounded-md border bg-white" data-testid={`${testidPrefix}-root`}>
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b bg-muted/30">
        <div className="text-xs">
          <strong>{selectedVendors.length}</strong> vendor{selectedVendors.length === 1 ? '' : 's'} picked
          {loaded && <span className="ml-2 text-muted-foreground">· {matches.length} match this requirement</span>}
        </div>
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs"
          onClick={() => setExpanded(v => !v)}
          data-testid={`${testidPrefix}-toggle`}>
          {expanded ? 'Done' : (selectedVendors.length ? 'Edit' : 'Pick vendors')}
        </Button>
      </div>
      {/* Selected chips — always visible so the operator sees who's queued. */}
      {selectedVendors.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2.5 py-2 border-b">
          {selectedVendors.map(v => {
            const noContact = !((v.email || '').trim()) && !((v.phone || '').trim());
            const noEmail = !((v.email || '').trim());
            return (
              <span key={v.id} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${noContact ? 'bg-red-50 border-red-200 text-red-800' : noEmail ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}
                data-testid={`${testidPrefix}-chip-${v.id}`}>
                {v.name}{noEmail ? (noContact ? ' (no contact)' : ' (WA only)') : ''}
                <button type="button" onClick={() => togglePick(v.id)} className="hover:text-destructive">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          <button type="button" onClick={clearAll}
            className="text-[11px] text-muted-foreground underline ml-1"
            data-testid={`${testidPrefix}-clear`}>
            Clear all
          </button>
        </div>
      )}
      {expanded && (
        <div className="p-2.5 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input className="pl-7 h-9 text-sm" placeholder="Search by name, location, email"
              value={search} onChange={e => setSearch(e.target.value)}
              data-testid={`${testidPrefix}-search`} />
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{loaded ? `${matches.length} match` : 'Loading…'}</span>
            <button type="button" className="text-emerald-700 hover:underline"
              onClick={pickAllVisible} data-testid={`${testidPrefix}-pick-all`}>
              Pick all visible
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto border rounded-md">
            {!loaded ? (
              <div className="text-xs text-muted-foreground p-3 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading vendors…
              </div>
            ) : matches.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3">No vendors match this requirement. Adjust location or accommodation type above.</p>
            ) : matches.slice(0, 100).map(v => {
              const noContact = !((v.email || '').trim()) && !((v.phone || '').trim());
              const noEmail = !((v.email || '').trim());
              return (
                <div key={v.id} className="flex items-start gap-2 p-2 border-b last:border-b-0"
                  data-testid={`${testidPrefix}-row-${v.id}`}>
                  <Checkbox checked={sel.has(v.id)} onCheckedChange={() => togglePick(v.id)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <button type="button" className="text-left w-full" onClick={() => togglePick(v.id)}>
                      <div className="text-sm font-medium truncate">{v.name || '—'}</div>
                      <div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2 items-center">
                        {v.location && <span>{v.location}</span>}
                        {noContact
                          ? <span className="text-red-600">· no contact</span>
                          : (noEmail
                              ? <span className="text-amber-700">· WhatsApp only</span>
                              : <span className="text-emerald-700">· email + WA</span>)}
                      </div>
                    </button>
                    {/* Inline accommodation-type tagger — only for Hotel
                        vendors. Backfills the 6-tier taxonomy one tap at a
                        time so the next query gets a clean filter. */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {type === 'Hotel' && (
                        <AccommodationTypeTagger
                          vendor={v}
                          onTagged={(val) => { v.accommodation_type = val; }}
                        />
                      )}
                      {/* Inline contact backfill — every vendor type. Edit
                          name/email/phone without leaving the query. */}
                      <ContactTagger
                        vendor={v}
                        onUpdated={(fresh) => {
                          // Patch local cached vendor with returned row so
                          // the chip colour reflects new contact channels.
                          if (fresh) {
                            v.name = fresh.name; v.email = fresh.email; v.phone = fresh.phone;
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            {matches.length > 100 && (
              <div className="text-[10px] text-muted-foreground p-2">Showing first 100 — refine search to narrow.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// Comma/Enter-separated alias chips. Used to broaden vendor targeting
// (e.g. Cherrapunjee + Sohra, Jaigaon + Phuentsholing).
const AliasInput = ({ aliases = [], onChange, placeholder = 'Add alternate name…', testid }) => {
  const [draft, setDraft] = useState('');
  const add = (raw) => {
    const v = (raw || '').trim();
    if (!v) return;
    if ((aliases || []).map(a => a.toLowerCase()).includes(v.toLowerCase())) { setDraft(''); return; }
    onChange([...(aliases || []), v]);
    setDraft('');
  };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {(aliases || []).map(a => (
          <span key={a} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-muted border">
            {a}
            <button type="button" onClick={() => onChange(aliases.filter(x => x !== a))}
              className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${a}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <Input value={draft} className="h-9 text-sm" placeholder={placeholder}
        data-testid={testid}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); }
        }}
        onBlur={() => add(draft)} />
    </div>
  );
};

const HotelRow = ({ h, tour, onChange, onRemove, indexLabel }) => {
  // The override toggle lets a single halt diverge from the tour-level
  // defaults (rooms / adults / children / accommodation type / meal plan).
  // Without override, the email body composer fills these from Tour Details.
  const ov = h.overrides || {};
  const override = !!h.override;
  const inheritedAccTypes = override ? (ov.accommodation_types || []) : (tour?.accommodation_types || []);
  const inheritedMeal = override ? (ov.meal_plan || '') : (tour?.meal_plan || '');
  const inheritedRooms = override ? ov.rooms : tour?.rooms;
  const inheritedAdults = override ? ov.adults : tour?.adults;
  const inheritedChildren = override ? ov.children : tour?.children;
  const inheritedChildAges = override ? (ov.child_ages || []) : (tour?.child_ages || []);

  const setOv = (patch) => onChange({ ...h, overrides: { ...ov, ...patch } });
  const setOverrideChildren = (count) => {
    const n = Math.max(0, count || 0);
    const ages = Array.from({ length: n }, (_, i) => (ov.child_ages || [])[i] ?? '');
    setOv({ children: n, child_ages: ages });
  };

  const summarySub = [
    h.check_in ? `${h.check_in}${h.check_out ? ` → ${h.check_out}` : ''}` : '',
    h.nights ? `${h.nights}N` : '',
    (h.vendor_ids || []).length ? `${(h.vendor_ids || []).length} vendor${(h.vendor_ids || []).length > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(' · ');

  return (
    <CollapsibleBlock
      icon={Bed}
      title={h.location || indexLabel || 'New halt'}
      subtitle={summarySub}
      onRemove={onRemove}
      testid="hotel-row">
      <div>
        <Label className="text-xs">Overnight location</Label>
        <Input className="h-11" placeholder="Overnight location (e.g. Shillong)"
          value={h.location || ''} onChange={e => onChange({ ...h, location: e.target.value })}
          data-testid="hotel-location" />
      </div>

      <div>
        <Label className="text-xs">Alternate / nearby names (broaden vendor targeting)</Label>
        <AliasInput aliases={h.alt_locations || []}
          onChange={alts => onChange({ ...h, alt_locations: alts })}
          placeholder="e.g. Sohra (for Cherrapunjee)…"
          testid="hotel-alt-input" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Check-in</Label>
          <Input type="date" className="h-11" value={h.check_in || ''}
            onChange={e => onChange({ ...h, check_in: e.target.value })}
            data-testid="hotel-check-in" />
        </div>
        <div>
          <Label className="text-xs">Check-out</Label>
          <Input type="date" className="h-11" value={h.check_out || ''}
            onChange={e => onChange({ ...h, check_out: e.target.value })}
            data-testid="hotel-check-out" />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-xs">Nights</Label>
          <Input type="number" min="0" inputMode="numeric" className="h-11"
            value={h.nights ?? ''} onChange={e => onChange({ ...h, nights: parseInt(e.target.value || '0', 10) })}
            data-testid="hotel-nights" />
        </div>
      </div>

      {!h.check_in && !h.check_out && (
        <div>
          <Label className="text-xs">Season / period (when dates aren't fixed)</Label>
          <Input className="h-11" placeholder="e.g. Nov 2026 – Feb 2027, or Full year"
            value={h.season || ''} onChange={e => onChange({ ...h, season: e.target.value })}
            data-testid="hotel-season" />
          <p className="text-[10px] text-muted-foreground mt-0.5">Outreach will ask for seasonal / annual B2B rates for this period.</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Room category</Label>
          <Input className="h-11" value={h.room_category || ''}
            onChange={e => onChange({ ...h, room_category: e.target.value })}
            placeholder="Deluxe / Suite / Cottage"
            data-testid="hotel-room-category" />
        </div>
        <div>
          <Label className="text-xs">Price range filter (internal · ₹/room/night)</Label>
          <div className="grid grid-cols-2 gap-1.5">
            <Input type="number" min="0" inputMode="numeric" className="h-11" placeholder="From ₹"
              value={h.price_min ?? ''} onChange={e => onChange({ ...h, price_min: parseInt(e.target.value || '0', 10) })}
              data-testid="hotel-price-min" />
            <Input type="number" min="0" inputMode="numeric" className="h-11" placeholder="To ₹"
              value={h.price_max ?? ''} onChange={e => onChange({ ...h, price_max: parseInt(e.target.value || '0', 10) })}
              data-testid="hotel-price-max" />
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs">Special request</Label>
        <Input className="h-10" value={h.special_request || ''}
          onChange={e => onChange({ ...h, special_request: e.target.value })}
          data-testid="hotel-special" />
      </div>

      {/* In-card vendor picker — filtered to Hotel vendors matching this
          halt's location (+ aliases) and the inherited accommodation type
          taxonomy. The selected vendor IDs are stored on the halt itself
          so the operator only sees vendors picked for THIS halt when they
          fire the outreach button. */}
      <div>
        <Label className="text-xs">Hotels picked for this halt</Label>
        <VendorMultiPicker
          type="Hotel"
          locations={[h.location, ...(h.alt_locations || [])].filter(Boolean)}
          accommodationTypes={
            (h.override ? (h.overrides?.accommodation_types || []) : (tour?.accommodation_types || []))
          }
          selectedIds={h.vendor_ids || []}
          onChange={(ids) => onChange({ ...h, vendor_ids: ids })}
          testidPrefix="hotel-vp"
        />
      </div>

      {/* Inherited context badges — show what this halt will use in the
          email body when override is OFF. */}
      <div className="rounded-md bg-muted/40 border px-2 py-1.5 text-[11px] text-muted-foreground space-y-0.5">
        <div className="flex items-center justify-between">
          <span className="font-medium text-foreground">{override ? 'Override active' : 'Inherited from Tour Details'}</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <Checkbox checked={override}
              onCheckedChange={(v) => onChange({ ...h, override: !!v })}
              data-testid="hotel-override-toggle" />
            <span className="text-foreground">Override for this halt</span>
          </label>
        </div>
        {!override && (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            {inheritedRooms ? <span>Rooms: {inheritedRooms}</span> : null}
            {inheritedAdults ? <span>Adults: {inheritedAdults}</span> : null}
            {inheritedChildren ? <span>Children: {inheritedChildren}{(inheritedChildAges || []).filter(a => a !== '' && a !== null).length ? ` (ages ${(inheritedChildAges || []).filter(a => a !== '' && a !== null).join(', ')})` : ''}</span> : null}
            {(inheritedAccTypes || []).length ? <span>Acc: {inheritedAccTypes.join(' / ')}</span> : null}
            {inheritedMeal ? <span>Meal: {inheritedMeal}</span> : null}
            {!inheritedRooms && !inheritedAdults && !inheritedChildren && !(inheritedAccTypes || []).length && !inheritedMeal
              ? <span>Set Tour Details fields above and they appear here.</span> : null}
          </div>
        )}
      </div>

      {override && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Rooms (this halt)</Label>
              <Input type="number" min="0" inputMode="numeric" className="h-10"
                value={ov.rooms ?? ''} onChange={e => setOv({ rooms: parseInt(e.target.value || '0', 10) })}
                data-testid="hotel-ov-rooms" />
            </div>
            <div>
              <Label className="text-xs">Meal plan (this halt)</Label>
              <Select value={ov.meal_plan || ''} onValueChange={v => setOv({ meal_plan: v })}>
                <SelectTrigger className="h-10" data-testid="hotel-ov-meal-plan"><SelectValue placeholder="Pick one" /></SelectTrigger>
                <SelectContent>
                  {MEAL_PLANS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Adults (this halt)</Label>
              <Input type="number" min="0" inputMode="numeric" className="h-10"
                value={ov.adults ?? ''} onChange={e => setOv({ adults: parseInt(e.target.value || '0', 10) })}
                data-testid="hotel-ov-adults" />
            </div>
            <div>
              <Label className="text-xs">Children (this halt)</Label>
              <Input type="number" min="0" inputMode="numeric" className="h-10"
                value={ov.children ?? ''} onChange={e => setOverrideChildren(parseInt(e.target.value || '0', 10))}
                data-testid="hotel-ov-children" />
            </div>
          </div>
          {(ov.children || 0) > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {(ov.child_ages || []).map((age, i) => (
                <Input key={i} type="number" min="0" max="17" inputMode="numeric" className="h-9"
                  value={age ?? ''} placeholder={`#${i + 1}`}
                  onChange={e => {
                    const next = [...(ov.child_ages || [])];
                    next[i] = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                    setOv({ child_ages: next });
                  }}
                  data-testid={`hotel-ov-child-age-${i}`} />
              ))}
            </div>
          )}
          <div>
            <Label className="text-xs">Accommodation type (this halt)</Label>
            <ChipMulti value={ov.accommodation_types || []}
              onChange={v => setOv({ accommodation_types: v })}
              options={ACCOMMODATION_TYPES}
              testidPrefix="hotel-ov-acc" />
          </div>
        </div>
      )}
    </CollapsibleBlock>
  );
};

// required), guide language (multi-select). Supports multiple guides — different
// guides for different locations / different dates.
const GUIDE_LANGS = ['English', 'Hindi', 'Bengali', 'Marathi', 'Tamil', 'Telugu', 'Khasi', 'Garo'];

const LangChips = ({ value = [], onChange, options = GUIDE_LANGS }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map(o => {
      const active = (value || []).includes(o);
      return (
        <button key={o} type="button"
          onClick={() => onChange(active ? value.filter(x => x !== o) : [...(value || []), o])}
          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
            active ? 'bg-foreground text-background border-foreground'
                   : 'bg-white text-foreground border-border'
          }`}>
          {o}
        </button>
      );
    })}
  </div>
);

// "Approve & extract halts" — runs Gemini 3 Pro on an uploaded itinerary
// PDF, returns a proposed halts list `[{location, nights}, ...]`, and lets
// the operator confirm before halts overwrite the current Hotel
// Requirements rows.
const ExtractHaltsButton = ({ queryId, attachments, tourStart, budget, onHaltsExtracted }) => {
  const [open, setOpenLocal] = useState(false);
  const [selectedAttId, setSelectedAttId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [proposed, setProposed] = useState([]);  // [{location, nights}]
  const [err, setErr] = useState('');

  const pdfAtts = (attachments || []).filter(a => (a.content_type || '').includes('pdf'));

  const runExtract = async (attId) => {
    setLoading(true); setErr(''); setProposed([]);
    try {
      const r = await fetch(`${API}/pollen/queries/${queryId}/extract-halts-from-pdf`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ attachment_id: attId }),
      });
      const data = await r.json();
      if (!r.ok) { setErr(data.detail || 'Extraction failed'); return; }
      setProposed(Array.isArray(data.halts) ? data.halts : []);
    } catch (e) {
      setErr(String(e && e.message ? e.message : e));
    } finally {
      setLoading(false);
    }
  };

  const start = () => {
    setOpenLocal(true);
    if (pdfAtts.length === 1) {
      setSelectedAttId(pdfAtts[0].id || pdfAtts[0].storage_path);
      runExtract(pdfAtts[0].id || pdfAtts[0].storage_path);
    }
  };

  const updatePropAt = (idx, patch) => {
    setProposed(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };

  return (
    <>
      <Button variant="outline" className="w-full h-11 border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100"
        data-testid="hotel-extract-from-pdf" onClick={start}>
        <Sparkles className="w-4 h-4 mr-1" /> Approve & extract halts from itinerary PDF
      </Button>
      <Dialog open={open} onOpenChange={setOpenLocal}>
        <DialogContent className="max-w-md" data-testid="extract-halts-dialog">
          <DialogHeader><DialogTitle>Extract halts from itinerary</DialogTitle></DialogHeader>
          {pdfAtts.length > 1 && (
            <div className="space-y-2">
              <Label className="text-xs">Pick the itinerary PDF</Label>
              <Select value={selectedAttId || ''} onValueChange={(v) => { setSelectedAttId(v); runExtract(v); }}>
                <SelectTrigger className="h-10" data-testid="extract-pick-pdf"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>
                  {pdfAtts.map(a => (
                    <SelectItem key={a.id || a.storage_path} value={a.id || a.storage_path}>{a.filename}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading PDF…
            </div>
          )}
          {err && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm px-3 py-2">{err}</div>
          )}
          {!loading && proposed.length > 0 && (
            <div className="space-y-2 max-h-72 overflow-auto">
              <Label className="text-xs">Proposed halts (edit before committing)</Label>
              {proposed.map((p, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`extract-row-${i}`}>
                  <Input className="h-9 flex-1" value={p.location || ''}
                    onChange={e => updatePropAt(i, { location: e.target.value })}
                    placeholder="Location" />
                  <Input className="h-9 w-20" type="number" min="0" value={p.nights ?? 1}
                    onChange={e => updatePropAt(i, { nights: parseInt(e.target.value || '0', 10) })}
                    placeholder="Nights" />
                  <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-destructive"
                    onClick={() => setProposed(prev => prev.filter((_, j) => j !== i))}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="w-full h-9"
                onClick={() => setProposed(prev => [...prev, { location: '', nights: 1 }])}>
                <Plus className="w-4 h-4 mr-1" /> Add row
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenLocal(false)}>Cancel</Button>
            <Button
              disabled={loading || proposed.length === 0}
              data-testid="extract-commit"
              onClick={() => {
                onHaltsExtracted(proposed.filter(p => (p.location || '').trim()));
                setOpenLocal(false);
              }}>
              Commit {proposed.length || ''} halts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const GuideRow = ({ g, onChange, onRemove, sourceBrief = '', anchorDate = '', indexLabel }) => {
  const canAutoFill = !!(sourceBrief && anchorDate && g.date_from && g.date_to);
  const refreshBrief = () => {
    const sliced = sliceBriefByDateRange(sourceBrief, anchorDate, g.date_from, g.date_to);
    if (sliced) onChange({ ...g, brief: sliced });
    else toast.info('No matching days in itinerary brief for this guide window.');
  };
  const summarySub = [
    (g.date_from || g.date_to) ? `${g.date_from || '—'} → ${g.date_to || '—'}` : '',
    (g.languages || []).join(', '),
    (g.vendor_ids || []).length ? `${(g.vendor_ids || []).length} vendor${(g.vendor_ids || []).length > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(' · ');
  return (
    <CollapsibleBlock
      icon={UserCheck}
      title={g.location || indexLabel || 'New guide'}
      subtitle={summarySub}
      onRemove={onRemove}
      testid="guide-row">
      <div>
        <Label className="text-xs">Location</Label>
        <Input className="h-11" placeholder="Location (e.g. Shillong)"
          value={g.location || ''} onChange={e => onChange({ ...g, location: e.target.value })}
          data-testid="guide-location" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Date from</Label>
          <Input type="date" className="h-11" value={g.date_from || ''}
            onChange={e => onChange({ ...g, date_from: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Date to</Label>
          <Input type="date" className="h-11" value={g.date_to || ''}
            onChange={e => onChange({ ...g, date_to: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Guests</Label>
          <Input type="number" min="0" inputMode="numeric" className="h-11"
            value={g.guests ?? ''}
            onChange={e => onChange({ ...g, guests: parseInt(e.target.value || '0', 10) })} />
        </div>
        <div>
          <Label className="text-xs">Languages</Label>
          <div className="pt-1.5"><LangChips value={g.languages || []}
            onChange={langs => onChange({ ...g, languages: langs })} /></div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs">Brief itinerary (only the part needing a guide)</Label>
          {canAutoFill && (
            <Button type="button" size="sm" variant="outline"
              className="h-7 text-[11px] border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100"
              onClick={refreshBrief}
              data-testid="guide-brief-refresh">
              <Sparkles className="w-3 h-3 mr-1" /> Pull from itinerary
            </Button>
          )}
        </div>
        <Textarea rows={2} maxLength={1000} className="text-sm"
          value={g.brief || ''} onChange={e => onChange({ ...g, brief: e.target.value })}
          placeholder="Auto-filled from the brief itinerary based on guide dates."
          data-testid="guide-brief" />
      </div>
      <div>
        <Label className="text-xs">Special request</Label>
        <Input className="h-10" value={g.special || ''}
          onChange={e => onChange({ ...g, special: e.target.value })} />
      </div>
      {/* In-card vendor picker — Guides matching this assignment's location. */}
      <div>
        <Label className="text-xs">Guides picked for this assignment</Label>
        <VendorMultiPicker
          type="Guide"
          locations={[g.location].filter(Boolean)}
          selectedIds={g.vendor_ids || []}
          onChange={(ids) => onChange({ ...g, vendor_ids: ids })}
          testidPrefix="guide-vp"
        />
      </div>
    </CollapsibleBlock>
  );
};

// ----- PackageBlock: single block (per spec - one package per query) --------
// Brief itinerary, days, dates from/to, car type, guide-requirement details,
// special request. Package "includes" chips let the operator declare which
// services are bundled (only hotel | hotel+guide | hotel+transport | all).
const PACKAGE_INCLUDE_OPTS = [
  { key: 'hotel',           label: 'Hotel' },
  { key: 'transportation',  label: 'Transportation' },
  { key: 'guide',           label: 'Guide' },
];

const PackageBlock = ({ p, onChange, haltLocations = [], queryId = null, attachments = [] }) => {
  const safe = p || {};
  const [extracting, setExtracting] = useState(false);
  const pdfAtt = (attachments || []).find(a => (a.content_type || '').includes('pdf'));
  const extractBrief = async () => {
    if (!pdfAtt || !queryId) return;
    setExtracting(true);
    try {
      const r = await fetch(`${API}/pollen/queries/${queryId}/extract-brief-itinerary-from-pdf`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ attachment_id: pdfAtt.id || pdfAtt.storage_path }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Extract failed'); return; }
      if (data.brief) {
        onChange({ ...safe, brief: data.brief });
        toast.success('Brief itinerary extracted');
      } else {
        toast.error('Empty response from LLM');
      }
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setExtracting(false);
    }
  };
  const toggleInc = (k) => {
    const list = safe.includes || [];
    onChange({ ...safe, includes: list.includes(k) ? list.filter(x => x !== k) : [...list, k] });
  };
  return (
    <CollapsibleBlock
      icon={Package}
      title="Package quote"
      subtitle={[(safe.includes || []).join(' + ') || 'Configure', (safe.vendor_ids || []).length ? `${safe.vendor_ids.length} vendor${safe.vendor_ids.length > 1 ? 's' : ''}` : ''].filter(Boolean).join(' · ')}
      testid="package-block">
      <div>
        <Label className="text-xs">Package includes</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {PACKAGE_INCLUDE_OPTS.map(o => {
            const active = (safe.includes || []).includes(o.key);
            return (
              <button key={o.key} type="button" onClick={() => toggleInc(o.key)}
                className={`text-xs px-2.5 py-1 rounded-full border ${
                  active ? 'bg-foreground text-background border-foreground'
                         : 'bg-white text-foreground border-border'
                }`}
                data-testid={`pkg-inc-${o.key}`}>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs">Brief itinerary</Label>
          {pdfAtt && queryId && (
            <Button type="button" size="sm" variant="outline"
              className="h-7 text-[11px] border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100"
              disabled={extracting} onClick={extractBrief}
              data-testid="pkg-brief-extract">
              <Sparkles className="w-3 h-3 mr-1" />
              {extracting ? 'Reading…' : 'Extract from PDF'}
            </Button>
          )}
        </div>
        <Textarea rows={3} maxLength={1000} className="text-sm"
          value={safe.brief || ''} onChange={e => onChange({ ...safe, brief: e.target.value })}
          placeholder="Day 1: Shillong arrival. Day 2: Cherrapunjee. Day 3: Dawki + return."
          data-testid="pkg-brief" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Days</Label>
          <Input type="number" min="0" inputMode="numeric" className="h-11"
            value={safe.days ?? ''}
            onChange={e => onChange({ ...safe, days: parseInt(e.target.value || '0', 10) })}
            data-testid="pkg-days" />
        </div>
        <div>
          <Label className="text-xs">Date from</Label>
          <Input type="date" className="h-11" value={safe.date_from || ''}
            onChange={e => onChange({ ...safe, date_from: e.target.value })}
            data-testid="pkg-from" />
        </div>
        <div>
          <Label className="text-xs">Date to</Label>
          <Input type="date" className="h-11" value={safe.date_to || ''}
            onChange={e => onChange({ ...safe, date_to: e.target.value })}
            data-testid="pkg-to" />
        </div>
      </div>
      {(safe.includes || []).includes('transportation') && (
        <div>
          <Label className="text-xs">Car type</Label>
          <Input className="h-11" value={safe.car_type || ''}
            onChange={e => onChange({ ...safe, car_type: e.target.value })}
            placeholder="Sedan / SUV / Innova / Tempo Traveller"
            data-testid="pkg-cartype" />
        </div>
      )}
      {(safe.includes || []).includes('guide') && (
        <div>
          <Label className="text-xs">Guide requirement details</Label>
          <Textarea rows={2} maxLength={1000} className="text-sm"
            value={safe.guide_brief || ''}
            onChange={e => onChange({ ...safe, guide_brief: e.target.value })}
            placeholder="Brief — only the segments where a guide is needed"
            data-testid="pkg-guide-brief" />
        </div>
      )}
      <div>
        <Label className="text-xs">Special request</Label>
        <Input className="h-10" value={safe.special || ''}
          onChange={e => onChange({ ...safe, special: e.target.value })}
          data-testid="pkg-special" />
      </div>
      {/* In-card vendor picker — Operators / fleet companies for packaged
          quotes. Locations OR-match against all halt locations on the
          query (set externally by the parent). */}
      <div>
        <Label className="text-xs">Operators picked for this package</Label>
        <VendorMultiPicker
          type={["Agent", "Transport"]}
          locations={haltLocations}
          selectedIds={safe.vendor_ids || []}
          onChange={(ids) => onChange({ ...safe, vendor_ids: ids })}
          testidPrefix="pkg-vp"
        />
      </div>
    </CollapsibleBlock>
  );
};
export const TaskBlock = ({ task, team, onUpdate, onRemove, onShare }) => {
  const history = task.assignment_history || [];
  const reassignedCount = history.length;
  const assignedTo = task.assigned_to && task.assigned_to !== '__unassigned' ? task.assigned_to : '';
  const teamMember = (team || []).find(m => (m.email || m.phone || m.name) === assignedTo);
  const subtitle = [
    teamMember ? teamMember.name : (assignedTo || 'Unassigned'),
    task.status || 'Pending',
    task.due_date ? `Due ${task.due_date.slice(0, 10)}` : '',
  ].filter(Boolean).join(' · ');
  const badge = (
    <>
      {reassignedCount > 0 && (
        <span
          title={history.map(h => `${h.from} → ${h.to}`).join('\n')}
          className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-sky-100 text-sky-800 text-[10px] font-semibold border border-sky-200"
          data-testid={`task-reassigned-${task.id || task.task_type}`}
        >
          ↻ {reassignedCount > 1 ? `× ${reassignedCount}` : ''}
        </span>
      )}
      {task.priority && task.priority !== 'Normal' && (
        <Badge className={`text-[10px] mr-1 ${task.priority === 'Urgent' ? 'bg-rose-600' : task.priority === 'High' ? 'bg-amber-500' : 'bg-slate-400'} text-white`}>
          {task.priority}
        </Badge>
      )}
    </>
  );
  return (
    <CollapsibleBlock
      icon={Tag}
      title={task.task_type}
      subtitle={subtitle}
      badge={badge}
      onRemove={onRemove || undefined}
      testid={`task-block-${task.id || task.task_type}`}>
      <div>
        <Label className="text-xs">Assigned to</Label>
        <Select value={task.assigned_to || ''} onValueChange={val => onUpdate({ ...task, assigned_to: val })}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Choose team member" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__unassigned">Unassigned</SelectItem>
            {(team || []).map(m => (
              <SelectItem key={m.id} value={m.email || m.phone || m.name}>
                {m.name}{m.role ? ` — ${m.role}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Textarea
        rows={2}
        value={task.notes || ''}
        onChange={e => onUpdate({ ...task, notes: e.target.value })}
        placeholder="Task notes"
        className="text-sm"
        data-testid={`task-notes-${task.id || task.task_type}`}
      />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Priority</Label>
          <Select value={task.priority || 'Normal'} onValueChange={val => onUpdate({ ...task, priority: val })}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Due date</Label>
          <Input type="datetime-local" className="h-11" value={task.due_date || ''}
            onChange={e => onUpdate({ ...task, due_date: e.target.value })} />
        </div>
      </div>
      <div>
        <Label className="text-xs">Status</Label>
        <Select value={task.status || 'Pending'} onValueChange={val => onUpdate({ ...task, status: val })}>
          <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {onShare && (
        <Button variant="outline" size="sm" className="w-full h-10 mt-1" onClick={onShare} data-testid="task-share">
          <Share2 className="w-4 h-4 mr-2" />Share Task
        </Button>
      )}
    </CollapsibleBlock>
  );
};

// ============================================================================
// Workspace — handles BOTH new and existing queries (route id presence)
// ============================================================================
const QueryWorkspace = () => {
  const navigate = useNavigate();
  const params = useParams();
  const isNew = !params.id;

  // Sections collapsed by default per spec.
  const [open, setOpen] = useState({ guest: false, tour: false, vqt: false, hotels: false, vehicles: false, guides: false, pkg: false, docs: false, timeline: false, brochure: false });
  const [brochureOpen, setBrochureOpen] = useState(false);
  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  const [source, setSource] = useState('');
  const [customer, setCustomer] = useState(null); // {id, name, phone}
  const [quickName, setQuickName] = useState('');
  const [quickPhone, setQuickPhone] = useState('');
  const [guest, setGuest] = useState({ name: '', mobile: '', email: '', billing_address: '', meal_preference: '', mixed_meal_counts: {} });
  const [tour, setTour] = useState({ adults: '', children: '', child_ages: [], rooms: '', budget_per_room_per_night: '', nights: '', start_date: '', end_date: '', accommodation_types: [], meal_plan: '' });
  const [vehicles, setVehicles] = useState([]);
  const [hotels, setHotels] = useState([]);     // [{location, alt_locations[], accommodation_types[], check_in, check_out, rooms, meal_plan, adults, children, child_ages[], room_category, price_min, price_max, special_request}]
  const [guides, setGuides] = useState([]);     // [{location, date_from, date_to, guests, languages[], brief, special}]
  const [pkg, setPkg] = useState(null);          // {brief, days, date_from, date_to, car_type, includes[], guide_brief, special} or null
  const [vendorQueryTypes, setVendorQueryTypes] = useState([]); // ['hotel','transportation','guide','package']
  const [packageIncludes, setPackageIncludes] = useState([]);   // subset of ['hotel','transport','guide']
  const [notes, setNotes] = useState('');
  const [selectedTaskTypes, setSelectedTaskTypes] = useState([]);
  const [tasks, setTasks] = useState({}); // { [task_type]: { ...task } }
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);

  // Existing-query state
  const [existing, setExisting] = useState(null);
  const [existingTasks, setExistingTasks] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [zoho, setZoho] = useState({ quotation_url: '', invoice_url: '' });
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [generatingUploadLink, setGeneratingUploadLink] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  // Stage C — Outreach composer (opens BulkOutreachSheet pre-filled with
  // query requirements). The sheet logs every send & attaches the vendors
  // to this query.
  const [outreachOpen, setOutreachOpen] = useState(false);
  const [attachedVendors, setAttachedVendors] = useState([]);
  const [outreachPrefill, setOutreachPrefill] = useState(null);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  // WhatsApp launcher — when the package picks >1 vendor we open a chooser
  // dialog so the operator picks who to chat with first. {text, targets}.
  const [waLaunch, setWaLaunch] = useState(null);
  // Per-section outreach dispatchers — single source of truth so both the
  // per-card button AND the global "Fire all outreach" dispatch panel share
  // the exact same pre-fill logic (type/location/aliases/subject/body/IDs).
  const fireHotelOutreach = useCallback((h, i) => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    const label = h.location ? `HOTEL · ${h.location}` : `HOTEL · Halt ${i + 1}`;
    const inherited = inheritedHaltFields(h, tour);
    setOutreachPrefill({
      type: 'Hotel',
      location: h.location || '',
      locationAliases: [...(h.alt_locations || [])],
      accommodationTypes: inherited.accommodation_types || [],
      subject: `[${qry}][${label}] Quote request — Pollen Dots`,
      body: draftHotelBody(qry, h, tour, guest, attachments),
      vendorIds: h.vendor_ids || [],
    });
    setOutreachOpen(true);
  }, [existing, tour, guest, attachments]);

  const fireVehicleOutreach = useCallback((v, i) => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    const label = vehicleLegLabel(v);
    setOutreachPrefill({
      type: 'Driver',
      location: v.transfer_from || '',
      locationAliases: v.transfer_to ? [v.transfer_to] : [],
      accommodationTypes: [],
      subject: `[${qry}][VEHICLE · ${label}] Quote request — Pollen Dots`,
      body: draftVehicleBody(qry, v, tour, guest, attachments),
      vendorIds: v.vendor_ids || [],
    });
    setOutreachOpen(true);
  }, [existing, tour, guest, attachments]);

  const fireGuideOutreach = useCallback((g, i) => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    const label = g.location ? `GUIDE · ${g.location}` : `GUIDE · #${i + 1}`;
    setOutreachPrefill({
      type: 'Guide',
      location: g.location || '',
      locationAliases: [],
      accommodationTypes: [],
      subject: `[${qry}][${label}] Quote request — Pollen Dots`,
      body: draftGuideBody(qry, g, tour),
      vendorIds: g.vendor_ids || [],
    });
    setOutreachOpen(true);
  }, [existing, tour]);

  const firePackageOutreach = useCallback(() => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    setOutreachPrefill({
      type: 'Transport',
      location: hotels[0]?.location || '',
      locationAliases: hotels.slice(1).map(h => h.location).filter(Boolean),
      accommodationTypes: tour.accommodation_types || [],
      subject: `[${qry}][PACKAGE] Quote request — Pollen Dots`,
      body: draftPackageBody(qry, { tour, guest, hotels, vehicles, guides, pkg, packageIncludes, attachments }),
      vendorIds: pkg?.vendor_ids || [],
    });
    setOutreachOpen(true);
  }, [existing, hotels, tour, guest, vehicles, guides, pkg, packageIncludes, attachments]);

  // One-click WhatsApp-friendly brief: copies a compact, emoji-marked
  // version of the consolidated package note to the clipboard so the
  // operator can paste it into WhatsApp/Telegram without reformatting.
  const copyPackageWhatsApp = useCallback(async () => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    const text = draftPackageWhatsApp(qry, { tour, guest, hotels, vehicles, guides, pkg, packageIncludes, attachments });
    try {
      await navigator.clipboard.writeText(text);
      toast.success('WhatsApp brief copied — paste it directly into the chat.');
    } catch {
      // Fallback for browsers without clipboard API: select & copy via a
      // temporary textarea.
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success('WhatsApp brief copied.'); }
      catch { toast.error('Could not copy — long-press the textarea to copy manually.'); }
      document.body.removeChild(ta);
    }
  }, [existing, tour, guest, hotels, vehicles, guides, pkg, packageIncludes, attachments]);

  // Open the brief directly inside WhatsApp / WhatsApp Web. Pulls the
  // phone numbers of the package's picked vendors; if multiple are
  // picked, a chooser dialog asks the operator which vendor to message
  // first. Falls back to clipboard-copy if WhatsApp Web is blocked.
  const launchWhatsApp = useCallback(async (vendorIds, text) => {
    if (!vendorIds || !vendorIds.length) {
      toast.error('Pick a vendor on this card first.');
      return;
    }
    const targets = [];
    await Promise.all(vendorIds.map(async (vid) => {
      try {
        const r = await fetch(`${API}/pollen/vendors/${vid}`, {
          credentials: 'include', headers: authHeaders(),
        });
        if (!r.ok) return;
        const v = await r.json();
        const phone = normalisePhone(v.phone || '');
        if (phone) targets.push({ id: vid, name: v.name || 'Vendor', phone, type: v.type || '' });
      } catch { /* skip */ }
    }));
    if (!targets.length) {
      toast.error('No picked vendor has a WhatsApp number on file.');
      return;
    }
    if (targets.length === 1) {
      const url = `https://wa.me/${targets[0].phone}?text=${encodeURIComponent(personaliseWhatsAppText(text, targets[0]))}`;
      window.open(url, '_blank', 'noopener');
      return;
    }
    setWaLaunch({ text, targets });
  }, []);

  const openPackageWhatsApp = useCallback(async () => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    const text = draftPackageWhatsApp(qry, { tour, guest, hotels, vehicles, guides, pkg, packageIncludes, attachments });
    await launchWhatsApp(pkg?.vendor_ids || [], text);
  }, [existing, tour, guest, hotels, vehicles, guides, pkg, packageIncludes, attachments, launchWhatsApp]);

  const openHotelWhatsApp = useCallback(async (h) => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    await launchWhatsApp(h.vendor_ids || [], draftHotelWhatsApp(qry, h, tour, guest));
  }, [existing, tour, guest, launchWhatsApp]);

  const openVehicleWhatsApp = useCallback(async (v) => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    await launchWhatsApp(v.vendor_ids || [], draftVehicleWhatsApp(qry, v, tour, guest));
  }, [existing, tour, guest, launchWhatsApp]);

  const openGuideWhatsApp = useCallback(async (g) => {
    if (!existing) return;
    const qry = existing.query_id || 'QRY';
    await launchWhatsApp(g.vendor_ids || [], draftGuideWhatsApp(qry, g, tour));
  }, [existing, tour, launchWhatsApp]);

  // Pending outreach queue — flattens every card that has at least one
  // vendor picked into a single list. Drives the sticky badge + dispatch
  // panel, and is the single source for "Fire all outreach".
  const outreachQueue = useMemo(() => {
    const items = [];
    (hotels || []).forEach((h, i) => {
      if ((h.vendor_ids || []).length) {
        items.push({
          key: `H${i}`, kind: 'hotel',
          label: h.location || `Halt ${i + 1}`,
          subLabel: h.check_in ? `${h.check_in} → ${h.check_out || '—'}` : '',
          count: h.vendor_ids.length,
          fire: () => fireHotelOutreach(h, i),
        });
      }
    });
    (vehicles || []).forEach((v, i) => {
      if ((v.vendor_ids || []).length) {
        items.push({
          key: `V${i}`, kind: 'vehicle',
          label: vehicleLegLabel(v),
          subLabel: v.category || '',
          count: v.vendor_ids.length,
          fire: () => fireVehicleOutreach(v, i),
        });
      }
    });
    (guides || []).forEach((g, i) => {
      if ((g.vendor_ids || []).length) {
        items.push({
          key: `G${i}`, kind: 'guide',
          label: g.location || `Guide #${i + 1}`,
          subLabel: (g.languages || []).join(', '),
          count: g.vendor_ids.length,
          fire: () => fireGuideOutreach(g, i),
        });
      }
    });
    if (pkg && (pkg.vendor_ids || []).length) {
      items.push({
        key: 'P', kind: 'package',
        label: 'Packaged quote',
        subLabel: (packageIncludes || []).join(' + '),
        count: pkg.vendor_ids.length,
        fire: () => firePackageOutreach(),
      });
    }
    return items;
  }, [hotels, vehicles, guides, pkg, packageIncludes, fireHotelOutreach, fireVehicleOutreach, fireGuideOutreach, firePackageOutreach]);

  const outreachQueueTotal = outreachQueue.reduce((s, it) => s + it.count, 0);


  // Sync child_ages to children count
  useEffect(() => {
    const n = parseInt(tour.children || '0', 10) || 0;
    if (n !== (tour.child_ages || []).length) {
      setTour(t => ({ ...t, child_ages: Array.from({ length: n }, (_, i) => (t.child_ages || [])[i] || '') }));
    }
  }, [tour.children, tour.child_ages]);

  // Auto-fill each Guide's `brief` from the package brief itinerary, sliced
  // by guide date range. Only fills when guide.brief is empty so manual
  // edits are never overwritten. Use the "Pull from itinerary" button on
  // a guide row to force a re-slice after the operator edits dates.
  const _pkgBrief = pkg?.brief || '';
  const _anchor = tour.start_date || '';
  useEffect(() => {
    if (!_pkgBrief || !_anchor || !guides.length) return;
    let changed = false;
    const next = guides.map(g => {
      if ((g.brief || '').trim()) return g;
      if (!g.date_from || !g.date_to) return g;
      const sliced = sliceBriefByDateRange(_pkgBrief, _anchor, g.date_from, g.date_to);
      if (!sliced) return g;
      changed = true;
      return { ...g, brief: sliced };
    });
    if (changed) {
      setGuides(next);
      if (existing) patchSilent({ guides: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_pkgBrief, _anchor, guides.length, guides.map(g => `${g.date_from}|${g.date_to}`).join('#')]);

  // Roll up each Guide's brief into pkg.guide_brief so the consolidated
  // Package outreach email reflects every guide segment automatically.
  // Only fills when pkg.guide_brief is empty.
  useEffect(() => {
    if (!pkg) return;
    if (!(pkg.includes || []).includes('guide')) return;
    if ((pkg.guide_brief || '').trim()) return;
    const valid = (guides || []).filter(g => (g.brief || '').trim() || g.location);
    if (!valid.length) return;
    const merged = valid.map((g, i) => {
      const loc = g.location || `Guide ${i + 1}`;
      const dates = (g.date_from || g.date_to) ? ` (${g.date_from || '—'} → ${g.date_to || '—'})` : '';
      const brief = (g.brief || '').trim();
      return `• ${loc}${dates}${brief ? `:\n${brief}` : ''}`;
    }).join('\n\n');
    if (!merged) return;
    setPkg(p => ({ ...(p || {}), guide_brief: merged }));
    if (existing) patchSilent({ package_req: { ...pkg, guide_brief: merged } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guides, pkg?.includes, pkg?.guide_brief]);

  // Team for assigned-to
  useEffect(() => {
    fetch(`${API}/pollen/team`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setTeam)
      .catch(() => setTeam([]));
  }, []);

  // Load existing query
  const fetchExisting = useCallback(async (withSpinner = false) => {
    if (isNew) return;
    if (withSpinner) setLoading(true);
    try {
      const res = await fetch(`${API}/pollen/queries/${params.id}`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (!res.ok) {
        toast.error('Query not found');
        navigate('/pollen-dots/queries');
        return;
      }
      const data = await res.json();
      const q = data.query;
      setExisting(q);
      setExistingTasks(data.tasks || []);
      setTimeline(data.timeline || []);
      setAttachments(data.attachments || []);
      setZoho({ quotation_url: q.quotation_url || '', invoice_url: q.invoice_url || '' });
      setSource(q.source || '');
      setCustomer(q.customer_id ? { id: q.customer_id, name: q.customer_name, phone: q.customer_phone } : null);
      setGuest(q.guest_details || { name: '', mobile: '', email: '', billing_address: '', meal_preference: '', mixed_meal_counts: {} });
      setTour(q.tour_details || { adults: '', children: '', child_ages: [], rooms: '', budget_per_room_per_night: '', nights: '', start_date: '', end_date: '', accommodation_types: [], meal_plan: '' });
      setVehicles(q.vehicles || []);
      setHotels(q.hotels || []);
      setGuides(q.guides || []);
      setPkg(q.package_req || null);
      setVendorQueryTypes(q.vendor_query_types || []);
      setPackageIncludes(q.package_includes || []);
      setNotes(q.requirement_notes || '');
      setAttachedVendors(q.attached_vendors || []);
    } finally { if (withSpinner) setLoading(false); }
  }, [isNew, params.id, navigate]);
  useEffect(() => { fetchExisting(true); }, [fetchExisting]);

  // Toggle task type
  const toggleTaskType = (tt) => {
    setSelectedTaskTypes(prev => {
      const has = prev.includes(tt);
      if (has) {
        setTasks(t => { const copy = { ...t }; delete copy[tt]; return copy; });
        return prev.filter(x => x !== tt);
      } else {
        setTasks(t => ({ ...t, [tt]: { task_type: tt, assigned_to: '', notes: '', priority: 'Normal', due_date: '', status: 'Pending' } }));
        return [...prev, tt];
      }
    });
  };

  // Save new query
  const saveNew = async () => {
    if (!source) { toast.error('Pick a query source first'); return; }
    const hasNotes = (notes || '').trim().length > 0;
    const hasAttachment = false; // attachments not in this batch
    const hasTasks = selectedTaskTypes.length > 0;
    if (!hasNotes && !hasAttachment && !hasTasks) {
      toast.error('Add notes, an attachment, or pick at least one task before saving.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        source,
        customer_id: customer?.id || null,
        customer_name: customer ? '' : quickName,
        customer_phone: customer ? '' : quickPhone,
        guest_details: guest,
        tour_details: {
          ...tour,
          adults: tour.adults ? parseInt(tour.adults, 10) : null,
          children: tour.children ? parseInt(tour.children, 10) : null,
          rooms: tour.rooms ? parseInt(tour.rooms, 10) : null,
          budget_per_room_per_night: tour.budget_per_room_per_night ? parseFloat(tour.budget_per_room_per_night) : null,
          nights: tour.nights ? parseInt(tour.nights, 10) : null,
        },
        vehicles,
        requirement_notes: notes,
        tasks: selectedTaskTypes.map(tt => tasks[tt]).filter(Boolean),
      };
      const res = await fetch(`${API}/pollen/queries`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(`Query ${data.query_id} created`);
      navigate(`/pollen-dots/queries/${data.id}`);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  // Patch update for existing query — saves on field blur for each section.
  const patchUpdate = async (patch) => {
    if (!existing) return;
    try {
      const res = await fetch(`${API}/pollen/queries/${existing.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Update failed');
      }
      // Merge locally instead of refetching — a refetch overwrote whichever
      // field the operator had moved on to typing in.
      setExisting(prev => (prev ? { ...prev, ...patch } : prev));
    } catch (e) { toast.error(e.message); }
  };

  // Debounced, silent save for the requirement sections (hotels / vehicles /
  // guides / package). Local state is already the source of truth while the
  // operator types, so we persist in the background WITHOUT re-fetching the
  // query — a refetch here resets every field + collapsible and made the page
  // appear to "refresh" on each keystroke.
  const patchTimers = useRef({});
  const patchSilent = (patch) => {
    if (!existing) return;
    const key = Object.keys(patch).sort().join(',');
    clearTimeout(patchTimers.current[key]);
    patchTimers.current[key] = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/pollen/queries/${existing.id}`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(patch),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || 'Update failed');
        }
      } catch (e) { toast.error(e.message); }
    }, 600);
  };

  const addTaskToExisting = async (taskType) => {
    const res = await fetch(`${API}/pollen/queries/${existing.id}/tasks`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ task_type: taskType }),
    });
    if (res.ok) fetchExisting();
    else toast.error('Failed to add task');
  };
  const updateExistingTask = (task) => {
    // Optimistic local update + debounced save — saving per keystroke with a
    // full refetch made the page "refresh" while typing task notes.
    setExistingTasks(ts => ts.map(t => (t.id === task.id ? task : t)));
    const key = `task:${task.id}`;
    clearTimeout(patchTimers.current[key]);
    patchTimers.current[key] = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/pollen/queries/${existing.id}/tasks/${task.id}`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            task_type: task.task_type,
            assigned_to: task.assigned_to === '__unassigned' ? '' : task.assigned_to,
            notes: task.notes,
            priority: task.priority,
            due_date: task.due_date,
            status: task.status,
          }),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || 'Task save failed');
        }
      } catch (e) {
        toast.error(e.message);
      }
    }, 600);
  };
  const deleteExistingTask = async (tid) => {
    await fetch(`${API}/pollen/queries/${existing.id}/tasks/${tid}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    fetchExisting();
  };

  // --- Attachments / Itinerary share ---------------------------------------
  // Always called from the existing-query view (no upload on the new-query
  // form to keep first-save fast on mobile).
  const uploadFile = async (fileBlob, kind, label = '') => {
    if (!existing) return;
    if (!fileBlob) return;
    if (fileBlob.size > 15 * 1024 * 1024) { toast.error('File too large (max 15 MB)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', fileBlob);
      fd.append('kind', kind);
      if (label) fd.append('label', label);
      const res = await fetch(`${API}/pollen/queries/${existing.id}/attachments`, {
        method: 'POST', credentials: 'include',
        headers: authHeaders(), body: fd,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Upload failed');
      }
      toast.success(`${kind === 'itinerary' ? 'Itinerary' : 'File'} uploaded`);
      fetchExisting();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const deleteAttachment = async (aid) => {
    if (!existing) return;
    if (!window.confirm('Delete this attachment?')) return;
    await fetch(`${API}/pollen/queries/${existing.id}/attachments/${aid}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    fetchExisting();
  };

  const copyShareUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied');
    } catch (e) { toast.error('Could not copy'); }
  };

  const shareItineraryWhatsApp = (att) => {
    if (!existing || !att.share_url) return;
    const name = existing.customer_name || 'there';
    const lines = [
      `Hi ${name},`,
      '',
      'Your personalised itinerary is ready — tap below to view & download:',
      att.share_url,
      '',
      '— Pollen Dots',
      'Travel · Connect · Care',
    ];
    openShareSheet({
      title: 'Pollen Dots · Itinerary',
      text: lines.join('\n'),
      url: att.share_url,
      fallbackPhone: existing.customer_phone,
    });
  };

  // Generic Zoho (Quotation / Invoice) share — uses the device's native
  // share sheet so the operator can pick ANY contact (WhatsApp / Telegram /
  // SMS / Email). We never put the raw Zoho URL into the message.
  const shareZohoOnWhatsApp = (label, shareUrl) => {
    if (!existing || !shareUrl) return;
    const name = existing.customer_name || 'there';
    const lower = label.toLowerCase();
    const lines = label === 'Quotation' ? [
      `Hi ${name},`,
      '',
      `Your ${lower} is ready — tap below to review:`,
      shareUrl,
      '',
      'Let us know if anything needs tweaking. Once you confirm, we lock the trip in for you.',
      '',
      '— Pollen Dots',
      'Travel · Connect · Care',
    ] : [
      `Hi ${name},`,
      '',
      `Your ${lower} is ready — tap below to view & pay securely:`,
      shareUrl,
      '',
      '— Pollen Dots',
      'Travel · Connect · Care',
    ];
    openShareSheet({
      title: `Pollen Dots · ${label}`,
      text: lines.join('\n'),
      url: shareUrl,
      fallbackPhone: existing.customer_phone,
    });
  };

  // --- Native share sheet helper -------------------------------------------
  // Prefers `navigator.share` (Android / iOS / desktop with Web Share API) —
  // this is what surfaces the "Send to…" contacts picker. Falls back to a
  // WhatsApp deep-link when the API isn't available, AFTER normalising the
  // phone to E.164 (strips leading zeros so wa.me doesn't reject it).
  const openShareSheet = async ({ title, text, url, fallbackPhone }) => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        // Pass ONLY text. WhatsApp (and most share targets) concatenate
        // `url` to `text` on send — passing both produces a duplicate URL.
        await navigator.share({ title, text });
        return;
      } catch (e) {
        // user cancelled — don't fall back
        if (e && (e.name === 'AbortError' || /cancel/i.test(e.message || ''))) return;
      }
    }
    // Fallback: normalize phone → E.164 then open WhatsApp web/app.
    const digits = (fallbackPhone || '').replace(/\D+/g, '').replace(/^0+/, '');
    let e164 = digits;
    if (digits && digits.length === 10) e164 = `91${digits}`;
    else if (digits.length === 12 && digits.startsWith('91')) e164 = digits;
    const wa = e164
      ? `https://wa.me/${e164}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, '_blank', 'noopener');
  };

  // --- Convert to Tour -----------------------------------------------------
  const handleConvertToTour = async () => {
    if (!existing) return;
    if (!existing.customer_id) { toast.error('Attach a customer first'); return; }
    if (existing.tour_id) { navigate(`/pollen-dots/tour/${existing.tour_id}`); return; }
    const trip = window.prompt(
      'Trip name (leave blank for auto):',
      `${existing.customer_name || 'Trip'} — ${(existing.tour_details || {}).start_date || 'Custom'}`,
    );
    if (trip === null) return;
    setConverting(true);
    try {
      const res = await fetch(`${API}/pollen/queries/${existing.id}/convert-to-tour`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ trip_name: (trip || '').trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Convert failed');
      if (data.enriched_from_invoice) {
        toast.success(`Tour created · ${data.trip_name} (auto-filled from invoice)`);
      } else {
        toast.success(`Tour created · ${data.trip_name}`);
      }
      navigate(`/pollen-dots/tour/${data.tour_id}`);
    } catch (e) { toast.error(e.message); }
    finally { setConverting(false); }
  };

  // --- Customer upload link ------------------------------------------------
  // Idempotent: tapping the button on a query that already has a slug just
  // returns the existing URL — so it's safe to retry.
  const handleGenerateUploadLink = async () => {
    if (!existing) return;
    setGeneratingUploadLink(true);
    try {
      const res = await fetch(`${API}/pollen/queries/${existing.id}/upload-link`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to generate link');
      toast.success('Customer upload link ready');
      fetchExisting();
    } catch (e) { toast.error(e.message); }
    finally { setGeneratingUploadLink(false); }
  };

  const shareUploadLink = () => {
    if (!existing?.customer_upload_url) return;
    const name = existing.customer_name || 'there';
    const lines = [
      `Hi ${name},`,
      '',
      'Please upload your ID / passport / age proof via the secure link below:',
      existing.customer_upload_url,
      '',
      'This link is private to your trip with us.',
      '',
      '— Pollen Dots',
      'Travel · Connect · Care',
    ];
    openShareSheet({
      title: 'Pollen Dots · Document upload',
      text: lines.join('\n'),
      url: existing.customer_upload_url,
      fallbackPhone: existing.customer_phone,
    });
  };

  // --- Close query ---------------------------------------------------------
  // Manual terminal state. Different from Delete — the row stays in the
  // database for reporting / future "Reopen" workflows; just stops
  // showing as Active in pipeline views.
  const handleCloseQuery = async () => {
    if (!existing) return;
    if (!window.confirm(`Close query ${existing.query_id}?\n\nIt'll move to the "Closed" stage. You can still view it later.`)) return;
    setClosing(true);
    try {
      const res = await fetch(`${API}/pollen/queries/${existing.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status: 'Closed' }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Close failed');
      }
      toast.success('Query closed');
      fetchExisting();
    } catch (e) { toast.error(e.message); }
    finally { setClosing(false); }
  };

  // --- Delete query --------------------------------------------------------
  // Hard delete with a strong double-tap confirmation. Refused server-side
  // if the query has already been converted to a Tour.
  const handleDeleteQuery = async () => {
    if (!existing) return;
    const label = `${existing.query_id} · ${existing.customer_name || ''}`.trim();
    if (!window.confirm(`Delete query ${label}?\n\nThis removes its tasks, timeline, and attachments. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/pollen/queries/${existing.id}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Delete failed');
      }
      toast.success('Query deleted');
      navigate('/pollen-dots/queries');
    } catch (e) { toast.error(e.message); }
    finally { setDeleting(false); }
  };

  // Share task via WhatsApp — manual send, same mechanism as celebrations.
  const shareTask = (task) => {
    if (!existing) return;
    const base = window.location.origin;
    const taskUrl = `${base}/pollen-dots/queries/${existing.id}#task-${task.id}`;
    const lines = [];
    if (existing.customer_name) {
      lines.push(`Customer: ${existing.customer_name}`);
    } else {
      lines.push(`Query: ${existing.query_id}`);
    }
    lines.push(`Task: ${task.task_type}`);
    if (task.notes) lines.push(`Notes: ${task.notes}`);
    lines.push(`Open Task: ${taskUrl}`);
    const msg = lines.join('\n');
    const targetPhone = (task.assigned_to || '').replace(/\D+/g, '');
    const e164 = targetPhone.length === 10 ? `91${targetPhone}` : targetPhone;
    const waUrl = e164
      ? `https://wa.me/${e164}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank', 'noopener');
  };

  const mealMixed = guest.meal_preference === 'Mixed Group';
  const showLoader = !isNew && loading;

  if (showLoader) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-3 pb-28 lg:pb-6">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => navigate('/pollen-dots/queries')}>
            <ArrowLeft className="w-4 h-4 mr-1" />Queries
          </Button>
          <div className="flex-1 min-w-0">
            {existing ? (
              <>
                <h1 className="text-lg font-semibold truncate" data-testid="qry-title">{existing.query_id}</h1>
                <p className="text-xs text-muted-foreground truncate">
                  {existing.customer_name || existing.customer_phone || 'Provisional'} · {existing.source} · {existing.status}
                </p>
              </>
            ) : (
              <h1 className="text-lg font-semibold" data-testid="qry-title-new">New Query</h1>
            )}
          </div>
          {existing && (
            <Badge
              variant="outline"
              className={STATUS_COLORS[existing.status] || 'bg-amber-100 text-amber-900'}
              data-testid="qry-status-badge"
            >
              {existing.status}
            </Badge>
          )}
        </div>

        {/* SOURCE */}
        <Card data-testid="section-source">
          <CardContent className="p-4 space-y-2">
            <Label className="text-sm font-medium">Query Source *</Label>
            <Select value={source} onValueChange={val => {
              setSource(val);
              if (existing) patchUpdate({ source: val });
            }}>
              <SelectTrigger className="h-12 text-base" data-testid="source-select"><SelectValue placeholder="Where did this come from?" /></SelectTrigger>
              <SelectContent>
                {SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* CUSTOMER ATTACH */}
        <Card data-testid="section-customer">
          <CardContent className="p-4 space-y-2">
            <Label className="text-sm font-medium">Customer</Label>
            <CustomerSearch
              value={customer}
              onPick={async c => {
                setCustomer(c);
                if (existing && c) patchUpdate({ customer_id: c.id });
                // Auto-populate Guest Details from the customer record when
                // the operator picks an existing customer. We only fill the
                // BLANK fields so any data the operator has already typed
                // into Guest Details is preserved.
                if (c?.id) {
                  try {
                    const r = await fetch(`${API}/pollen/customers/${c.id}`, {
                      credentials: 'include', headers: authHeaders(),
                    });
                    if (r.ok) {
                      const full = await r.json();
                      const next = {
                        ...guest,
                        name: guest.name || full.name || c.name || '',
                        mobile: guest.mobile || full.phone || c.phone || '',
                        email: guest.email || full.email || '',
                        billing_address: guest.billing_address || full.billing_address || full.address || '',
                        meal_preference: guest.meal_preference || full.meal_preference || '',
                      };
                      setGuest(next);
                      if (existing) patchUpdate({ guest_details: next });
                    }
                  } catch (e) { /* non-fatal — operator can fill manually */ }
                }
              }}
              onQuickName={setQuickName}
              onQuickPhone={setQuickPhone}
            />
          </CardContent>
        </Card>

        {/* PENDING OUTREACH BADGE — sticky reminder of vendors picked across
            every requirement card. Click to expand a dispatch panel that
            walks the operator through each card's outreach in one tap. */}
        {existing && outreachQueueTotal > 0 && (
          <button type="button"
            data-testid="dispatch-open"
            onClick={() => setDispatchOpen(true)}
            className="w-full sticky top-2 z-10 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-900 shadow-sm px-3 py-2.5 flex items-center justify-between gap-2 active:scale-[0.99] transition-transform">
            <span className="flex items-center gap-2 min-w-0">
              <Megaphone className="w-4 h-4 shrink-0" />
              <span className="text-sm font-semibold truncate">
                {outreachQueueTotal} vendor{outreachQueueTotal === 1 ? '' : 's'} queued
              </span>
              <span className="text-[11px] text-emerald-700 truncate hidden sm:inline">
                · {outreachQueue.length} section{outreachQueue.length === 1 ? '' : 's'} ready
              </span>
            </span>
            <span className="text-[12px] font-medium bg-emerald-700 text-white px-2 py-1 rounded-full whitespace-nowrap">
              Send outreach
            </span>
          </button>
        )}

        {/* GUEST DETAILS */}
        <Section
          title="Guest Details"
          hint="Optional — name, mobile, email, meal preference"
          icon={Users}
          open={open.guest}
          onToggle={() => toggle('guest')}
          testid="section-guest"
        >
          <Input className="h-11" placeholder="Name" value={guest.name || ''}
            onChange={e => setGuest({ ...guest, name: e.target.value })}
            onBlur={() => existing && patchUpdate({ guest_details: guest })} />
          <Input className="h-11" type="tel" inputMode="tel" placeholder="Mobile Number" value={guest.mobile || ''}
            onChange={e => setGuest({ ...guest, mobile: e.target.value })}
            onBlur={() => existing && patchUpdate({ guest_details: guest })} />
          <Input className="h-11" type="text" inputMode="email" placeholder="Email Address" value={guest.email || ''}
            onChange={e => setGuest({ ...guest, email: e.target.value })}
            onBlur={() => existing && patchUpdate({ guest_details: guest })} />
          <Textarea rows={2} placeholder="Billing Address" value={guest.billing_address || ''}
            onChange={e => setGuest({ ...guest, billing_address: e.target.value })}
            onBlur={() => existing && patchUpdate({ guest_details: guest })} />
          <div>
            <Label className="text-xs">Meal Preference</Label>
            <Select value={guest.meal_preference || ''} onValueChange={val => {
              const next = { ...guest, meal_preference: val };
              setGuest(next);
              if (existing) patchUpdate({ guest_details: next });
            }}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Pick one" /></SelectTrigger>
              <SelectContent>{MEALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {mealMixed && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              {['veg', 'non_veg', 'eggitarian', 'jain', 'vegan'].map(k => (
                <div key={k}>
                  <Label className="text-xs">{k.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} count</Label>
                  <Input type="number" inputMode="numeric" className="h-11"
                    value={(guest.mixed_meal_counts || {})[k] || ''}
                    onChange={e => setGuest({ ...guest, mixed_meal_counts: { ...(guest.mixed_meal_counts || {}), [k]: parseInt(e.target.value || '0', 10) } })}
                    onBlur={() => existing && patchUpdate({ guest_details: guest })} />
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* TOUR DETAILS */}
        <Section
          title="Tour Details"
          hint="Pax, rooms, budget, dates"
          icon={Bed}
          open={open.tour}
          onToggle={() => toggle('tour')}
          testid="section-tour"
        >
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Adults</Label>
              <Input type="number" inputMode="numeric" className="h-11" value={tour.adults || ''}
                onChange={e => setTour({ ...tour, adults: e.target.value })}
                onBlur={() => existing && patchUpdate({ tour_details: { ...tour, adults: tour.adults ? parseInt(tour.adults, 10) : null } })} />
            </div>
            <div><Label className="text-xs">Children</Label>
              <Input type="number" inputMode="numeric" className="h-11" value={tour.children || ''}
                onChange={e => setTour({ ...tour, children: e.target.value })}
                onBlur={() => existing && patchUpdate({ tour_details: { ...tour, children: tour.children ? parseInt(tour.children, 10) : null } })} />
            </div>
          </div>
          {(parseInt(tour.children || '0', 10) > 0) && (
            <div className="grid grid-cols-3 gap-2 pt-1">
              {(tour.child_ages || []).map((age, i) => (
                <div key={i}><Label className="text-xs">Child {i + 1} age</Label>
                  <Input type="number" inputMode="numeric" className="h-11" value={age}
                    onChange={e => {
                      const arr = [...(tour.child_ages || [])]; arr[i] = e.target.value;
                      setTour({ ...tour, child_ages: arr });
                    }}
                    onBlur={() => existing && patchUpdate({ tour_details: tour })} />
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Rooms</Label>
              <Input type="number" inputMode="numeric" className="h-11" value={tour.rooms || ''}
                onChange={e => setTour({ ...tour, rooms: e.target.value })}
                onBlur={() => existing && patchUpdate({ tour_details: tour })} /></div>
            <div><Label className="text-xs">Nights</Label>
              <Input type="number" inputMode="numeric" className="h-11" value={tour.nights || ''}
                onChange={e => setTour({ ...tour, nights: e.target.value })}
                onBlur={() => existing && patchUpdate({ tour_details: tour })} /></div>
          </div>
          <div>
            <Label className="text-xs">Approx budget per room per night (₹)</Label>
            <Input type="number" inputMode="numeric" className="h-11" value={tour.budget_per_room_per_night || ''}
              onChange={e => setTour({ ...tour, budget_per_room_per_night: e.target.value })}
              onBlur={() => existing && patchUpdate({ tour_details: tour })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Tour start</Label>
              <Input type="date" className="h-11" value={tour.start_date || ''}
                onChange={e => setTour({ ...tour, start_date: e.target.value })}
                onBlur={() => existing && patchUpdate({ tour_details: tour })} /></div>
            <div><Label className="text-xs">Tour end</Label>
              <Input type="date" className="h-11" value={tour.end_date || ''}
                onChange={e => setTour({ ...tour, end_date: e.target.value })}
                onBlur={() => existing && patchUpdate({ tour_details: tour })} /></div>
          </div>
          {/* Shared accommodation type — inherited by every Hotel halt block
              unless that halt explicitly overrides it. Multi-select chips
              (6-tier taxonomy). */}
          <div>
            <Label className="text-xs">Accommodation type (shared across halts)</Label>
            <ChipMulti
              value={tour.accommodation_types || []}
              onChange={v => {
                const next = { ...tour, accommodation_types: v };
                setTour(next);
                existing && patchUpdate({ tour_details: next });
              }}
              options={ACCOMMODATION_TYPES}
              testidPrefix="tour-acc"
            />
          </div>
          {/* Shared meal plan — inherited unless a halt overrides. */}
          <div>
            <Label className="text-xs">Meal plan (shared across halts)</Label>
            <Select value={tour.meal_plan || ''}
              onValueChange={v => {
                const next = { ...tour, meal_plan: v };
                setTour(next);
                existing && patchUpdate({ tour_details: next });
              }}>
              <SelectTrigger className="h-11" data-testid="tour-meal-plan">
                <SelectValue placeholder="Pick one" />
              </SelectTrigger>
              <SelectContent>
                {MEAL_PLANS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </Section>

        {/* VENDOR QUERY TYPE — drives which sections must be filled, and
            also drives subject-line stamping and the outreach composer
            filter pre-fill. Multi-select. Package picks an additional
            scope sub-chip per spec. */}
        <Section
          title="Vendor Query Type"
          hint={
            vendorQueryTypes.length
              ? vendorQueryTypes.map(t => t === 'transportation' ? 'Transport' : t[0].toUpperCase() + t.slice(1)).join(' + ')
                + (vendorQueryTypes.includes('package') && packageIncludes.length
                    ? ` · package: ${packageIncludes.map(s => s[0].toUpperCase() + s.slice(1)).join(' + ')}`
                    : '')
              : 'Pick what to ask vendors for'
          }
          icon={Megaphone}
          open={open.vqt}
          onToggle={() => toggle('vqt')}
          testid="section-vendor-query-type"
        >
          <div className="space-y-2">
            <Label className="text-xs">Types (multi-select)</Label>
            <ChipMulti
              value={vendorQueryTypes}
              onChange={v => {
                setVendorQueryTypes(v);
                const nextIncludes = v.includes('package') ? packageIncludes : [];
                if (!v.includes('package')) setPackageIncludes([]);
                existing && patchUpdate({
                  vendor_query_types: v,
                  package_includes: nextIncludes,
                });
              }}
              options={['hotel', 'transportation', 'guide', 'package']}
              testidPrefix="vqt"
            />
          </div>
          {vendorQueryTypes.includes('package') && (
            <>
              <div className="space-y-2">
                <Label className="text-xs">Package includes (tick all that apply)</Label>
                <div className="flex flex-col gap-2">
                  {[
                    { k: 'hotel', l: 'Hotels' },
                    { k: 'transport', l: 'Transport' },
                    { k: 'guide', l: 'Guide' },
                  ].map(o => {
                    const checked = packageIncludes.includes(o.k);
                    const toggleInc = () => {
                      const next = checked
                        ? packageIncludes.filter(x => x !== o.k)
                        : [...packageIncludes, o.k];
                      setPackageIncludes(next);
                      existing && patchUpdate({ package_includes: next });
                    };
                    return (
                      <label key={o.k}
                        className="flex items-center gap-2 cursor-pointer select-none"
                        data-testid={`pkg-inc-${o.k}-label`}>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={toggleInc}
                          data-testid={`pkg-inc-${o.k}`}
                        />
                        <span className="text-sm">{o.l}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {/* Package operators picker — surfaced HERE (not just in the
                  Package Requirements card) because the most common flow is
                  to also fire a consolidated package quote to travel agents
                  / tour operators using the same hotels/vehicles/guides
                  already picked above. The selected IDs are stored on
                  `pkg.vendor_ids` so the existing Dispatch Panel + package
                  body composer pick them up automatically. */}
              {packageIncludes.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Tour operators / travel agents (for package quote)</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Body auto-assembled from Tour + ticked sections ({packageIncludes.map(s => s[0].toUpperCase() + s.slice(1)).join(' + ')}).
                  </p>
                  <VendorMultiPicker
                    type={["Agent", "Transport"]}
                    locations={[]}
                    selectedIds={(pkg && pkg.vendor_ids) || []}
                    onChange={(ids) => {
                      const next = { ...(pkg || {}), vendor_ids: ids };
                      setPkg(next);
                      existing && patchSilent({ package_req: next });
                    }}
                    testidPrefix="pkg-ops"
                  />
                </div>
              )}
            </>
          )}
        </Section>

        {/* HOTEL REQUIREMENTS — multi-row, one per overnight halt --------- */}
        <Section
          title="Hotel Requirements"
          hint={hotels.length ? `${hotels.length} halt${hotels.length === 1 ? '' : 's'} · ${sumHaltNights(hotels)}N` : 'Add halt-wise hotel requirements'}
          icon={Bed}
          open={open.hotels}
          onToggle={() => toggle('hotels')}
          testid="section-hotels"
        >
          {/* Validation banner — checks that the sum of halt nights matches
              the tour-level nights so check-ins tile end-to-end. */}
          {(() => {
            const haltN = sumHaltNights(hotels);
            const tourN = parseInt(tour.nights || '0', 10) || 0;
            if (!hotels.length) return null;
            if (tourN && haltN !== tourN) {
              return (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900" data-testid="halt-nights-mismatch">
                  Σ halt nights = <b>{haltN}</b> · Tour nights = <b>{tourN}</b>. Adjust nights so the halts tile end-to-end.
                </div>
              );
            }
            return (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900" data-testid="halt-nights-ok">
                Halts tile end-to-end · {haltN}N
              </div>
            );
          })()}

          {hotels.map((h, i) => (
            <div key={i} className="space-y-2">
              <HotelRow h={h} tour={tour}
                indexLabel={`Halt ${i + 1}`}
                onChange={nh => {
                  const next = [...hotels]; next[i] = nh;
                  // Auto-tile downstream halt dates if check-out or nights changed.
                  const retiled = retileHaltDates(next, tour.start_date);
                  setHotels(retiled);
                  existing && patchSilent({ hotels: retiled });
                }}
                onRemove={() => {
                  const next = hotels.filter((_, idx) => idx !== i);
                  const retiled = retileHaltDates(next, tour.start_date);
                  setHotels(retiled);
                  existing && patchSilent({ hotels: retiled });
                }} />
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid={`hotel-outreach-${i}`}
                  onClick={() => fireHotelOutreach(h, i)}>
                  <Megaphone className="w-3.5 h-3.5 mr-1" />
                  Send hotel outreach (this halt)
                </Button>
              )}
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid={`hotel-wa-${i}`}
                  onClick={() => openHotelWhatsApp(h)}>
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  Open in WhatsApp
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" className="w-full h-11" data-testid="hotel-add"
            onClick={() => {
              // New halts inherit dates by tiling onto the previous halt's
              // check-out (or tour start if none). 1 night default. If the
              // previous halt has no nights, fall back to its check_in to
              // avoid a degenerate 0-night row.
              const last = hotels[hotels.length - 1];
              const seedCheckIn = last?.check_out || last?.check_in || tour.start_date || '';
              const nights = 1;
              const checkOut = seedCheckIn ? addDays(seedCheckIn, nights) : '';
              const budget = parseFloat(tour.budget_per_room_per_night || '0') || 0;
              const next = [...hotels, {
                location: '', alt_locations: [],
                check_in: seedCheckIn, check_out: checkOut, nights,
                room_category: '', price_min: 0, price_max: budget,
                special_request: '',
                override: false, overrides: {},
              }];
              setHotels(next);
              existing && patchSilent({ hotels: next });
            }}>
            <Plus className="w-4 h-4 mr-1" />Add hotel (halt)
          </Button>

          {/* LLM auto-extract from any uploaded itinerary PDF attachment.
              Operator clicks → backend reads the PDF via Gemini 3 Pro →
              returns proposed halts → operator confirms in a small dialog →
              halts get written. Optional convenience. */}
          {existing && (attachments || []).some(a => (a.content_type || '').includes('pdf')) && (
            <ExtractHaltsButton
              queryId={existing.id}
              attachments={attachments || []}
              tourStart={tour.start_date}
              budget={parseFloat(tour.budget_per_room_per_night || '0') || 0}
              onHaltsExtracted={(extracted) => {
                // Convert extracted [{location, nights}] → tiled halts.
                const tiled = [];
                let cursor = tour.start_date || '';
                for (const ex of extracted) {
                  const n = Math.max(1, parseInt(ex.nights || 1, 10));
                  const co = cursor ? addDays(cursor, n) : '';
                  tiled.push({
                    location: ex.location || '', alt_locations: [],
                    check_in: cursor, check_out: co, nights: n,
                    room_category: '', price_min: 0,
                    price_max: parseFloat(tour.budget_per_room_per_night || '0') || 0,
                    special_request: '', override: false, overrides: {},
                  });
                  cursor = co;
                }
                setHotels(tiled);
                existing && patchSilent({ hotels: tiled });
              }}
            />
          )}
        </Section>

        {/* VEHICLE REQUIREMENTS */}
        <Section
          title="Vehicle Requirements"
          hint={vehicles.length ? `${vehicles.length} requirement${vehicles.length === 1 ? '' : 's'}` : 'Add vehicles needed'}
          icon={Bus}
          open={open.vehicles}
          onToggle={() => toggle('vehicles')}
          testid="section-vehicles"
        >
          {vehicles.map((v, i) => (
            <div key={i} className="space-y-2">
              <VehicleRow v={v}
                indexLabel={`Vehicle ${i + 1}`}
                onChange={nv => {
                  const next = [...vehicles]; next[i] = nv; setVehicles(next);
                  if (existing) patchSilent({ vehicles: next });
                }}
                onRemove={() => {
                  const next = vehicles.filter((_, idx) => idx !== i); setVehicles(next);
                  if (existing) patchSilent({ vehicles: next });
                }} />
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid={`vehicle-outreach-${i}`}
                  onClick={() => fireVehicleOutreach(v, i)}>
                  <Megaphone className="w-3.5 h-3.5 mr-1" />
                  Send transport outreach (this leg)
                </Button>
              )}
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid={`vehicle-wa-${i}`}
                  onClick={() => openVehicleWhatsApp(v)}>
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  Open in WhatsApp
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" className="w-full h-11" onClick={() => setVehicles([...vehicles, { category: '', qty: 1 }])}>
            <Plus className="w-4 h-4 mr-1" />Add vehicle
          </Button>
        </Section>

        {/* GUIDE REQUIREMENTS — multi-row, mirrors Vehicle Requirements ----- */}
        <Section
          icon={UserCheck}
          title="Guide Requirements"
          hint={guides.length ? `${guides.length} guide${guides.length === 1 ? '' : 's'}` : 'Add guides needed'}
          open={open.guides}
          onToggle={() => toggle('guides')}
          testid="section-guides"
        >
          {guides.map((g, i) => (
            <div key={i} className="space-y-2">
              <GuideRow g={g}
                sourceBrief={pkg?.brief || ''}
                anchorDate={tour.start_date || ''}
                indexLabel={`Guide ${i + 1}`}
                onChange={(ng) => {
                  const next = [...guides]; next[i] = ng; setGuides(next);
                  existing && patchSilent({ guides: next });
                }}
                onRemove={() => {
                  const next = guides.filter((_, idx) => idx !== i); setGuides(next);
                  existing && patchSilent({ guides: next });
                }} />
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid={`guide-outreach-${i}`}
                  onClick={() => fireGuideOutreach(g, i)}>
                  <Megaphone className="w-3.5 h-3.5 mr-1" />
                  Send guide outreach (this assignment)
                </Button>
              )}
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid={`guide-wa-${i}`}
                  onClick={() => openGuideWhatsApp(g)}>
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  Open in WhatsApp
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" className="w-full h-11" data-testid="guide-add"
            onClick={() => {
              const next = [...guides, {
                location: '', date_from: tour.start_date || '', date_to: tour.end_date || '',
                guests: (tour.adults || 0) + (tour.children || 0),
                languages: [], brief: '', special: '',
              }];
              setGuides(next);
              existing && patchSilent({ guides: next });
            }}>
            <Plus className="w-4 h-4 mr-1" />Add guide
          </Button>
        </Section>

        {/* PACKAGE REQUIREMENTS — single block ----------------------------- */}
        <Section
          icon={Package}
          title="Package Requirements"
          hint={pkg ? ((pkg.includes || []).join(' + ') || 'Configure package') : 'Configure if a packaged quote is needed'}
          open={open.pkg}
          onToggle={() => toggle('pkg')}
          testid="section-package"
        >
          {pkg ? (
            <>
              <PackageBlock p={pkg}
                haltLocations={(hotels || []).map(h => h.location).filter(Boolean)}
                queryId={existing?.id}
                attachments={attachments || []}
                onChange={(np) => { setPkg(np); existing && patchSilent({ package_req: np }); }} />
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid="pkg-outreach"
                  onClick={firePackageOutreach}>
                  <Megaphone className="w-3.5 h-3.5 mr-1" />
                  Send package outreach (consolidated)
                </Button>
              )}
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
                  data-testid="pkg-copy-whatsapp"
                  onClick={copyPackageWhatsApp}>
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  Copy WhatsApp brief
                </Button>
              )}
              {existing && (
                <Button size="sm" variant="outline"
                  className="w-full h-11 border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid="pkg-open-whatsapp"
                  onClick={openPackageWhatsApp}>
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  Open in WhatsApp
                </Button>
              )}
              <Button variant="ghost" className="w-full h-9 text-destructive" data-testid="pkg-remove"
                onClick={() => { setPkg(null); existing && patchSilent({ package_req: null }); }}>
                <Trash2 className="w-4 h-4 mr-1" />Remove package requirements
              </Button>
            </>
          ) : (
            <Button variant="outline" className="w-full h-11" data-testid="pkg-add"
              onClick={() => {
                const init = {
                  brief: notes || '',
                  days: tour.nights ? tour.nights + 1 : 0,
                  date_from: tour.start_date || '',
                  date_to: tour.end_date || '',
                  car_type: '', includes: ['hotel'], guide_brief: '', special: '',
                };
                setPkg(init);
                existing && patchSilent({ package_req: init });
              }}>
              <Plus className="w-4 h-4 mr-1" />Add package block
            </Button>
          )}
        </Section>

        {/* REQUIREMENT NOTES */}
        <Card data-testid="section-notes">
          <CardContent className="p-4 space-y-2">
            <Label className="text-sm font-medium">Requirement Notes</Label>
            <Textarea rows={6} placeholder="Paste call notes, customer requirements, draft itineraries…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={() => existing && notes !== existing.requirement_notes && patchUpdate({ requirement_notes: notes })}
              className="text-sm"
              data-testid="notes-textarea" />
          </CardContent>
        </Card>

        {/* TASKS — new mode = checklist, existing mode = task blocks */}
        {isNew ? (
          <Card data-testid="section-tasks-new">
            <CardContent className="p-4 space-y-3">
              <Label className="text-sm font-medium">Create Tasks (optional)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {TASK_TYPES.map(tt => (
                  <button
                    type="button"
                    key={tt}
                    onClick={() => toggleTaskType(tt)}
                    className={`text-left p-3 rounded-lg border min-h-[48px] flex items-center gap-2 ${selectedTaskTypes.includes(tt) ? 'border-emerald-500 bg-emerald-50' : 'hover:bg-muted/30'}`}
                    data-testid={`task-toggle-${tt}`}
                  >
                    <input type="checkbox" checked={selectedTaskTypes.includes(tt)} readOnly className="pointer-events-none" />
                    <span className="text-sm">{tt}</span>
                  </button>
                ))}
              </div>
              {selectedTaskTypes.length > 0 && (
                <div className="space-y-2 pt-2">
                  {selectedTaskTypes.map(tt => (
                    <TaskBlock
                      key={tt}
                      task={tasks[tt]}
                      team={team}
                      onUpdate={(t) => setTasks(ts => ({ ...ts, [tt]: t }))}
                      onRemove={() => toggleTaskType(tt)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="section-tasks">
            <CardContent className="p-4 space-y-3">
              <Label className="text-sm font-medium">Tasks</Label>
              {existingTasks.length === 0
                ? <p className="text-xs text-muted-foreground">No tasks yet.</p>
                : existingTasks.map(t => (
                  <TaskBlock
                    key={t.id}
                    task={t}
                    team={team}
                    onUpdate={updateExistingTask}
                    onRemove={() => deleteExistingTask(t.id)}
                    onShare={() => shareTask(t)}
                  />
                ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                {TASK_TYPES.map(tt => (
                  <Button key={tt} variant="outline" size="sm" className="h-10 justify-start" onClick={() => addTaskToExisting(tt)} data-testid={`task-add-${tt}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" />{tt}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* OUTREACH — query-driven (Stage C). Sits between Tasks and
            Documents so operators see the shortlist + send actions next
            to their workflow controls. */}
        {existing && (
          <Card data-testid="section-outreach">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Megaphone className="w-4 h-4 text-emerald-700" />Vendor Outreach</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Pick vendors (by location & type), draft once, fire WhatsApp / Email. Every send is logged and attached to this query.
              </p>
              <Button
                size="sm"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => { setOutreachPrefill(null); setOutreachOpen(true); }}
                data-testid="open-outreach-sheet"
              >
                <Megaphone className="w-4 h-4 mr-1" /> Compose & send to vendors
              </Button>

              {/* Attached vendors list */}
              {attachedVendors.length === 0 ? (
                <p className="text-xs text-muted-foreground">No vendors attached yet. Send an outreach to start a shortlist.</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-muted-foreground">Shortlist ({attachedVendors.length})</p>
                  {attachedVendors.map(av => (
                    <AttachedVendorRow
                      key={av.vendor_id}
                      attached={av}
                      queryId={existing.id}
                      onChange={(next) => setAttachedVendors(list => list.map(x => x.vendor_id === av.vendor_id ? { ...x, ...next } : x))}
                      onFollowUp={(vendor) => {
                        // Pre-target the outreach sheet to THIS vendor only,
                        // with Re: subject and a follow-up template body.
                        // followUpMode tells the sheet to use TO instead of
                        // BCC so the reply chain stays 1:1.
                        const qry = existing.query_id || 'QRY';
                        const lastSubj = vendor.last_subject || `[${qry}] Quote request — Pollen Dots`;
                        const reSubj = /^re:/i.test(lastSubj) ? lastSubj : `Re: ${lastSubj}`;
                        setOutreachPrefill({
                          type: vendor.vendor_type || '',
                          location: '', locationAliases: [], accommodationTypes: [],
                          subject: reSubj,
                          body: `Hello,\n\nFollowing up on my earlier note regarding query ${qry}. Could you please share the quote / confirmation at your earliest?\n\nLooking forward.`,
                          vendorIds: [vendor.vendor_id],
                          followUp: true,
                        });
                        setOutreachOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* BROCHURE STUDIO (existing only) — branded digital brochure
            customers can view at pollendots.com/connection/brochure/<slug>. */}
        {existing && (
          <Section
            icon={Sparkles}
            title="Brochure"
            hint="Branded photo + video share page"
            open={open.brochure}
            onToggle={() => toggle('brochure')}
            testid="section-brochure"
          >
            <Button size="sm" variant="outline"
                    className="w-full h-11 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                    onClick={() => setBrochureOpen(true)}
                    data-testid="open-brochure-studio">
              <Sparkles className="w-4 h-4 mr-1" /> Open Brochure Studio
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              Add photos / videos / music · share one Pollen Dots URL with the customer.
            </p>
          </Section>
        )}

        {/* ATTACHMENTS + ITINERARY (existing only) */}
        {existing && (
          <Section
            icon={Paperclip}
            title="Documents"
            hint={`${attachments.length} file${attachments.length === 1 ? '' : 's'}`}
            open={open.docs}
            onToggle={() => toggle('docs')}
            testid="section-attachments"
          >

              {/* Upload buttons — two clearly distinct CTAs so operators know
                  the difference between "the customer's itinerary" (shareable)
                  and any other doc (voucher, ID, ticket). */}
              <div className="grid grid-cols-2 gap-2">
                <label className="cursor-pointer h-11 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 flex items-center justify-center gap-2 text-sm font-medium text-amber-900" data-testid="upload-itinerary">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Upload Itinerary (PDF)
                  <input type="file" accept="application/pdf" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, 'itinerary'); e.target.value = ''; }}
                  />
                </label>
                <label className="cursor-pointer h-11 rounded-lg border bg-card hover:bg-muted/40 flex items-center justify-center gap-2 text-sm font-medium" data-testid="upload-other">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload Other Doc
                  <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, 'other'); e.target.value = ''; }}
                  />
                </label>
              </div>

              {/* Active itinerary share URL — only visible when a PDF is up */}
              {existing.latest_itinerary_url && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="active-itinerary">
                  <div className="text-[11px] uppercase tracking-wide text-amber-800 font-semibold">Active itinerary share link</div>
                  <div className="text-xs break-all font-mono text-amber-900">{existing.latest_itinerary_url}</div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" className="h-9" onClick={() => copyShareUrl(existing.latest_itinerary_url)} data-testid="copy-share-url">
                      <Copy className="w-3.5 h-3.5 mr-1" />Copy
                    </Button>
                    <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => {
                      const active = attachments.find(a => a.id === existing.latest_itinerary_id);
                      if (active) shareItineraryWhatsApp(active);
                    }} data-testid="whatsapp-share" title="Share via contacts">
                      <Share2 className="w-3.5 h-3.5 mr-1" />Share
                    </Button>
                    <Button size="sm" variant="ghost" className="h-9" asChild>
                      <a href={existing.latest_itinerary_url} target="_blank" rel="noopener noreferrer">
                        <Link2 className="w-3.5 h-3.5 mr-1" />Open
                      </a>
                    </Button>
                  </div>
                </div>
              )}

              {/* Customer upload link — generates a public branded URL the
                  customer can use to drop ID / passport / age proof files.
                  No auth on the public side; security is via the slug. */}
              <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2" data-testid="customer-upload-card">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-wide text-sky-800 font-semibold">Customer document upload</Label>
                  {!existing.customer_upload_url && (
                    <Button size="sm" variant="outline" className="h-8 border-sky-400 text-sky-800"
                      onClick={handleGenerateUploadLink} disabled={generatingUploadLink}
                      data-testid="generate-upload-link">
                      {generatingUploadLink ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
                      Generate link
                    </Button>
                  )}
                </div>
                {existing.customer_upload_url ? (
                  <>
                    <p className="text-[11px] text-sky-900/80">
                      Share this with the customer — they can upload ID / passport / age proof from their phone, no login required.
                    </p>
                    <div className="text-[11px] break-all font-mono text-sky-900">{existing.customer_upload_url}</div>
                    <div className="flex gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="h-8" onClick={() => copyShareUrl(existing.customer_upload_url)} data-testid="copy-upload-link">
                        <Copy className="w-3 h-3 mr-1" />Copy
                      </Button>
                      <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700 text-white" onClick={shareUploadLink} data-testid="share-upload-link">
                        <Share2 className="w-3 h-3 mr-1" />Share
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-[11px] text-sky-900/80">
                    Need ID/passport/age-proof from the customer? Generate a private upload link to share with them.
                  </p>
                )}
              </div>

              {/* All attachments list */}
              {attachments.length === 0 ? (
                <p className="text-xs text-muted-foreground">No documents uploaded yet.</p>
              ) : (
                <div className="space-y-2">
                  {attachments.map(a => (
                    <div key={a.id} className="rounded-lg border bg-card p-2.5 flex items-center gap-2" data-testid={`attachment-${a.id}`}>
                      <FileText className={`w-4 h-4 shrink-0 ${a.kind === 'itinerary' ? 'text-amber-700' : 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{a.filename || 'file'}</div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-[9px] h-4 px-1 capitalize">{a.kind}</Badge>
                          <span>{new Date(a.uploaded_at).toLocaleDateString()}</span>
                          {a.size && <span>{(a.size / 1024 / 1024).toFixed(1)} MB</span>}
                          {a.id === existing.latest_itinerary_id && (
                            <Badge className="text-[9px] h-4 px-1 bg-amber-600 text-white">Active</Badge>
                          )}
                        </div>
                      </div>
                      {a.kind === 'itinerary' && a.share_url && a.id !== existing.latest_itinerary_id && (
                        <Badge variant="outline" className="text-[9px]">v{attachments.filter(x => x.kind === 'itinerary' && x.uploaded_at <= a.uploaded_at).length}</Badge>
                      )}
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" asChild>
                        <a href={`${API}/pollen/queries/${existing.id}/attachments/${a.id}/download?auth=${encodeURIComponent(localStorage.getItem('session_token') || '')}`}
                          target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4" />
                        </a>
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => deleteAttachment(a.id)} data-testid={`attachment-delete-${a.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
          </Section>
        )}

        {/* DOCUMENTS — in-app Quotes / Receipts / Invoices (existing only) */}
        {existing && (
          <Card data-testid="section-documents">
            <CardContent className="p-4 space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" />Documents (Quotes · Receipts · Invoices)
              </Label>
              <DocumentsPanel queryId={existing.id} compact />
            </CardContent>
          </Card>
        )}

        {/* ZOHO LINKS (existing only) */}
        {existing && (
          <Card data-testid="section-zoho">
            <CardContent className="p-4 space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Receipt className="w-4 h-4" />Zoho Books Links
              </Label>
              <p className="text-[11px] text-muted-foreground -mt-1">
                Save the Zoho URL once. Then share a branded <span className="font-mono text-[10px]">pollendots.com</span> link via your phone&apos;s contact picker — they never see the raw Zoho link.
              </p>
              <div className="space-y-3">
                {/* QUOTATION */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Quotation URL (Zoho Books)</Label>
                  <div className="flex gap-2">
                    <Input className="h-11" placeholder="https://books.zoho.in/quote/..."
                      value={zoho.quotation_url}
                      onChange={e => setZoho(z => ({ ...z, quotation_url: e.target.value }))}
                      onBlur={() => { if (zoho.quotation_url !== (existing.quotation_url || '')) patchUpdate({ quotation_url: zoho.quotation_url }); }}
                      data-testid="zoho-quotation-input"
                    />
                    {zoho.quotation_url && (
                      <Button variant="outline" size="sm" className="h-11 px-3" asChild>
                        <a href={zoho.quotation_url} target="_blank" rel="noopener noreferrer" data-testid="zoho-quotation-open" title="Open in Zoho"><Link2 className="w-4 h-4" /></a>
                      </Button>
                    )}
                  </div>
                  {existing.quotation_share_url && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2.5 space-y-1.5" data-testid="quotation-share-card">
                      <div className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold">Pollen Dots share link</div>
                      <div className="text-[11px] break-all font-mono text-emerald-900">{existing.quotation_share_url}</div>
                      <div className="flex gap-1.5 flex-wrap">
                        <Button size="sm" variant="outline" className="h-8" onClick={() => copyShareUrl(existing.quotation_share_url)} data-testid="copy-quotation-share">
                          <Copy className="w-3 h-3 mr-1" />Copy
                        </Button>
                        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => shareZohoOnWhatsApp('Quotation', existing.quotation_share_url)}
                          data-testid="whatsapp-quotation" title="Share via contacts">
                          <Share2 className="w-3 h-3 mr-1" />Share
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                {/* INVOICE */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Invoice URL (Zoho Books)</Label>
                  <div className="flex gap-2">
                    <Input className="h-11" placeholder="https://books.zoho.in/invoice/..."
                      value={zoho.invoice_url}
                      onChange={e => setZoho(z => ({ ...z, invoice_url: e.target.value }))}
                      onBlur={() => { if (zoho.invoice_url !== (existing.invoice_url || '')) patchUpdate({ invoice_url: zoho.invoice_url }); }}
                      data-testid="zoho-invoice-input"
                    />
                    {zoho.invoice_url && (
                      <Button variant="outline" size="sm" className="h-11 px-3" asChild>
                        <a href={zoho.invoice_url} target="_blank" rel="noopener noreferrer" data-testid="zoho-invoice-open" title="Open in Zoho"><Link2 className="w-4 h-4" /></a>
                      </Button>
                    )}
                  </div>
                  {existing.invoice_share_url && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2.5 space-y-1.5" data-testid="invoice-share-card">
                      <div className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold">Pollen Dots share link</div>
                      <div className="text-[11px] break-all font-mono text-emerald-900">{existing.invoice_share_url}</div>
                      <div className="flex gap-1.5 flex-wrap">
                        <Button size="sm" variant="outline" className="h-8" onClick={() => copyShareUrl(existing.invoice_share_url)} data-testid="copy-invoice-share">
                          <Copy className="w-3 h-3 mr-1" />Copy
                        </Button>
                        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => shareZohoOnWhatsApp('Invoice', existing.invoice_share_url)}
                          data-testid="whatsapp-invoice" title="Share via contacts">
                          <Share2 className="w-3 h-3 mr-1" />Share
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CONVERT TO TOUR (existing only) */}
        {existing && (
          <Card data-testid="section-convert">
            <CardContent className="p-4 space-y-2">
              {existing.tour_id ? (
                <div className="flex items-center gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-emerald-700">This query is converted</div>
                    <div className="text-xs text-muted-foreground truncate">Tour ID: {existing.tour_id}</div>
                  </div>
                  <Button size="sm" onClick={() => navigate(`/pollen-dots/tour/${existing.tour_id}`)} data-testid="open-converted-tour">
                    <ArrowRightCircle className="w-4 h-4 mr-1" />Open Tour
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Locks this query as <span className="font-medium">Tour Created</span> and creates a new Tour pre-filled with the customer, dates, pax, rooms, budget, vehicles & notes.
                    {existing.invoice_url && (
                      <span className="block mt-1 text-emerald-700">
                        ✨ Invoice URL detected — hotels, transport, dates &amp; pricing will be auto-extracted from the Zoho invoice.
                      </span>
                    )}
                  </p>
                  <Button onClick={handleConvertToTour} disabled={converting || !existing.customer_id}
                    className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="convert-to-tour">
                    {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightCircle className="w-4 h-4 mr-2" />}
                    Convert to Tour
                  </Button>
                  {/* Close — terminal manual state for queries that never
                      converted. Sits between Convert and Delete so the
                      operator has a clean "give up gracefully" path. */}
                  {existing.status !== 'Closed' && (
                    <Button onClick={handleCloseQuery} disabled={closing}
                      variant="outline"
                      className="w-full h-10" data-testid="close-query">
                      {closing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                      Close query
                    </Button>
                  )}
                  {/* Destructive — sits below the primary action so it's
                      reachable but never an accidental tap target. */}
                  <Button onClick={handleDeleteQuery} disabled={deleting}
                    variant="ghost"
                    className="w-full h-10 text-destructive hover:bg-destructive/10" data-testid="delete-query">
                    {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Delete query
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* TIMELINE (existing only) */}
        {existing && (
          <Section
            icon={Clock}
            title="Timeline"
            hint={timeline.length ? `${timeline.length} event${timeline.length === 1 ? '' : 's'}` : 'No events yet'}
            open={open.timeline}
            onToggle={() => toggle('timeline')}
            testid="section-timeline"
          >
            {timeline.length === 0
              ? <p className="text-xs text-muted-foreground">No events yet.</p>
              : timeline.map(ev => (
                <div key={ev.id} className="flex items-start gap-2 text-xs">
                  <span className="text-muted-foreground whitespace-nowrap shrink-0">
                    {new Date(ev.ts).toLocaleString()}
                  </span>
                  <span className="flex-1 min-w-0 break-words">
                    <span className="font-medium">{ev.action.replace(/_/g, ' ')}</span>
                    {ev.details && <span className="text-muted-foreground break-all"> — {ev.details}</span>}
                  </span>
                </div>
              ))}
          </Section>
        )}

        {/* SAVE (new only) — fixed above mobile bottom-nav (which is h-16 / z-50) */}
        {isNew && (
          <div className="fixed left-0 right-0 bottom-16 lg:bottom-2 lg:sticky lg:left-auto lg:right-auto z-[55] px-3 sm:px-6 py-2 bg-background/95 backdrop-blur border-t lg:border-0 lg:bg-transparent lg:backdrop-blur-0 lg:py-0">
            <div className="max-w-2xl mx-auto">
              <Button onClick={saveNew} disabled={saving} className="w-full h-12 text-base bg-amber-600 hover:bg-amber-700 text-white shadow-lg" data-testid="qry-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Query
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Stage C — Bulk outreach for this query (auto-drafted from notes/tour data) */}
      {existing && (
        <BulkOutreachSheet
          open={outreachOpen}
          onOpenChange={(v) => { setOutreachOpen(v); if (!v) setOutreachPrefill(null); }}
          queryId={existing.id}
          defaultType={outreachPrefill?.type || ''}
          defaultLocation={outreachPrefill?.location || ''}
          defaultLocationAliases={outreachPrefill?.locationAliases || []}
          defaultAccommodationTypes={outreachPrefill?.accommodationTypes || []}
          defaultSubject={
            outreachPrefill?.subject
            || `[${existing.query_id || 'QRY'}] Quote request — Pollen Dots`
          }
          defaultBody={outreachPrefill?.body || draftBodyFromQuery(existing, tour, notes, vehicles)}
          preselectedVendorIds={outreachPrefill?.vendorIds || null}
          attachments={attachments || []}
          shareBase={PUBLIC_SHARE_BASE}
          followUpMode={!!outreachPrefill?.followUp}
          onSent={() => {
            // Re-fetch query so the attached_vendors shortlist refreshes
            fetchExisting();
          }}
        />
      )}

      {/* WhatsApp chooser — when the package picks >1 vendor with a valid
          phone, the operator picks which one to open the chat with. */}
      <Dialog open={!!waLaunch} onOpenChange={(v) => { if (!v) setWaLaunch(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Open WhatsApp with…</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {(waLaunch?.targets || []).map(t => (
              <Button key={t.id} variant="outline"
                className="w-full justify-start h-12 border-emerald-200 hover:bg-emerald-50"
                data-testid={`wa-target-${t.id}`}
                onClick={() => {
                  const url = `https://wa.me/${t.phone}?text=${encodeURIComponent(personaliseWhatsAppText(waLaunch.text, t))}`;
                  window.open(url, '_blank', 'noopener');
                  setWaLaunch(null);
                }}>
                <MessageCircle className="w-4 h-4 mr-2 text-emerald-700" />
                <span className="flex-1 text-left min-w-0">
                  <span className="block text-sm font-medium truncate">{t.name}</span>
                  <span className="block text-[11px] text-muted-foreground">+{t.phone}</span>
                </span>
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWaLaunch(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brochure Manager — opened from the Brochure section above. */}
      {existing && (
        <BrochureManager
          open={brochureOpen}
          onOpenChange={setBrochureOpen}
          ownerKind="query"
          ownerId={existing.id}
          defaultTitle={existing.query_id || existing.customer_name || ''}
        />
      )}

      {/* DISPATCH PANEL — opens a bottom Sheet listing every requirement
          card with picked vendors, each with its own "Send" button. The
          operator can fire them one-by-one (subject stamps differ per
          card) or hit "Fire all" to walk through them sequentially. */}
      {existing && (
        <Sheet open={dispatchOpen} onOpenChange={setDispatchOpen}>
          <SheetContent side="bottom" className="z-[80] max-h-[88vh] overflow-y-auto rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="dispatch-panel">
            <SheetHeader>
              <SheetTitle className="text-base">Send outreach</SheetTitle>
              <SheetDescription className="text-[12px]">
                {outreachQueueTotal} vendor{outreachQueueTotal === 1 ? '' : 's'} across {outreachQueue.length} section{outreachQueue.length === 1 ? '' : 's'}.
                Fire one at a time — each section opens its own message so the subject stamp routes replies back to that card.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-3 space-y-2">
              {outreachQueue.map(item => {
                const tone = item.kind === 'hotel'
                  ? 'border-emerald-200 bg-emerald-50'
                  : item.kind === 'vehicle'
                    ? 'border-sky-200 bg-sky-50'
                    : item.kind === 'guide'
                      ? 'border-violet-200 bg-violet-50'
                      : 'border-amber-200 bg-amber-50';
                const Icon = item.kind === 'hotel' ? Bed : item.kind === 'vehicle' ? Bus : item.kind === 'guide' ? UserCheck : Package;
                return (
                  <div key={item.key}
                    className={`rounded-xl border ${tone} p-3 flex items-center gap-3`}
                    data-testid={`dispatch-row-${item.key}`}>
                    <Icon className="w-5 h-5 shrink-0 text-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{item.label}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {item.subLabel ? `${item.subLabel} · ` : ''}{item.count} vendor{item.count === 1 ? '' : 's'} picked
                      </div>
                    </div>
                    <Button size="sm"
                      className="h-10 px-3 bg-foreground text-background hover:bg-foreground/90"
                      onClick={() => { setDispatchOpen(false); item.fire(); }}
                      data-testid={`dispatch-fire-${item.key}`}>
                      Send
                    </Button>
                  </div>
                );
              })}
              {outreachQueue.length === 0 && (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No vendors picked yet. Open a requirement card and tap &ldquo;Pick vendors&rdquo;.
                </p>
              )}
            </div>
            <div className="mt-4 pt-3 border-t flex gap-2">
              <Button variant="outline" className="flex-1 h-11" onClick={() => setDispatchOpen(false)} data-testid="dispatch-close">
                Done
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </Layout>
  );
};

// === Helpers ===============================================================

// Builds a sensible default outreach body from the query's requirement
// notes, tour details, and vehicle list. The operator can edit before
// firing — this is just the seed text.
function draftBodyFromQuery(query, tour, notes, vehicles) {
  const lines = [];
  lines.push('Hello,');
  lines.push('');
  lines.push('Greetings from Pollen Dots.');
  lines.push('');
  lines.push("We've received the following requirement from a guest and would appreciate your B2B quote:");
  lines.push('');
  if (tour?.start_date || tour?.end_date) {
    lines.push(`• Travel dates: ${tour.start_date || '—'} to ${tour.end_date || '—'}`);
  }
  const pax = [];
  if (tour?.adults) pax.push(`${tour.adults} adult(s)`);
  if (tour?.children) pax.push(`${tour.children} child(ren)`);
  if (pax.length) lines.push(`• Pax: ${pax.join(', ')}`);
  if (tour?.rooms) lines.push(`• Rooms needed: ${tour.rooms}`);
  if (tour?.nights) lines.push(`• Nights: ${tour.nights}`);
  if (tour?.budget_per_room_per_night) lines.push(`• Budget/room/night: ₹${tour.budget_per_room_per_night}`);
  if (Array.isArray(vehicles) && vehicles.length) {
    vehicles.forEach((v, i) => {
      if (!v) return;
      const parts = [v.category, v.route, v.from_date && `from ${v.from_date}`, v.to_date && `to ${v.to_date}`].filter(Boolean);
      if (parts.length) lines.push(`• Vehicle ${i + 1}: ${parts.join(' · ')}`);
    });
  }
  if ((notes || '').trim()) {
    lines.push('');
    lines.push('Notes:');
    lines.push(notes.trim());
  }
  lines.push('');
  lines.push('Please share your best rate, inclusions, and cancellation policy. We need quotes within 24 hours.');
  lines.push('');
  lines.push('Looking forward.');
  return lines.join('\n');
}

// Draft a hotel-specific outreach body (per-halt). The subject already
// carries `[QRY-...][HOTEL · {location}]`; the body restates the essentials
// (location, dates, rooms, accommodation type, meal plan, pax) so a vendor
// can quote without the operator typing it twice.
function draftHotelBody(qryShortId, h, tour, guest) {
  const lines = [];
  // Resolve inherited fields (tour defaults unless overridden).
  const o = h.override ? (h.overrides || {}) : {};
  const rooms = h.override ? o.rooms : tour?.rooms;
  const adults = h.override ? o.adults : tour?.adults;
  const children = h.override ? o.children : tour?.children;
  const childAges = h.override ? (o.child_ages || []) : (tour?.child_ages || []);
  const accTypes = h.override ? (o.accommodation_types || []) : (tour?.accommodation_types || []);
  const mealPlan = h.override ? (o.meal_plan || '') : (tour?.meal_plan || '');

  lines.push('Hello,');
  lines.push('');
  lines.push('Greetings from Pollen Dots.');
  lines.push('');
  lines.push(`We have a room requirement for query ${qryShortId} with the following details:`);
  lines.push('');
  if (h.location) {
    // Aliases are an INTERNAL vendor-targeting filter only; never expose
    // them to the vendor in the body — would confuse a Sohra vendor reading
    // about "Cherrapunjee". Show the halt's primary location only.
    lines.push(`• Location: ${h.location}`);
  }
  if (h.check_in || h.check_out) {
    const n = h.nights || '';
    lines.push(`• Check-in: ${h.check_in || '—'}  |  Check-out: ${h.check_out || '—'}${n ? `  (${n} night${n === 1 ? '' : 's'})` : ''}`);
  } else if (h.season) {
    lines.push(`• Stay period: ${h.season} — please share your best B2B seasonal rates for this period`);
  } else {
    lines.push('• Stay dates: Not fixed — please share your best B2B rates for the season / year');
  }
  if (rooms) lines.push(`• Rooms: ${rooms}`);
  if (adults || children) {
    const pax = [];
    if (adults) pax.push(`${adults} adult(s)`);
    if (children) {
      const ages = (childAges || []).filter(a => a !== '' && a !== null && a !== undefined);
      pax.push(`${children} child(ren)${ages.length ? ` — ages ${ages.join(', ')}` : ''}`);
    }
    if (pax.length) lines.push(`• Pax: ${pax.join(', ')}`);
  }
  if ((accTypes || []).length) {
    lines.push(`• Accommodation type: ${accTypes.join(' / ')}`);
  }
  if (h.room_category) lines.push(`• Room category: ${h.room_category}`);
  if (mealPlan) lines.push(`• Meal plan: ${mealPlan}`);
  // Budget is INTERNAL — used only to filter vendors. Never sent to vendor.
  if (h.special_request) {
    lines.push('');
    lines.push(`Special request: ${h.special_request}`);
  }
  if (guest?.meal_preference && !mealPlan) {
    lines.push(`• Guest meal preference: ${guest.meal_preference}`);
  }
  lines.push('');
  lines.push('Please share your best B2B rate, inclusions, and cancellation policy. We need a quote within 24 hours.');
  lines.push('');
  lines.push('Looking forward.');
  return lines.join('\n');
}

// ----- Per-card WhatsApp drafters -------------------------------------------
// Mirror draftHotelBody/draftVehicleBody/draftGuideBody but compact +
// emoji-marked for phone chat readability.

function draftHotelWhatsApp(qryShortId, h, tour, guest) {
  const o = h.override ? (h.overrides || {}) : {};
  const rooms = h.override ? o.rooms : tour?.rooms;
  const adults = h.override ? o.adults : tour?.adults;
  const children = h.override ? o.children : tour?.children;
  const accTypes = h.override ? (o.accommodation_types || []) : (tour?.accommodation_types || []);
  const mealPlan = h.override ? (o.meal_plan || '') : (tour?.meal_plan || '');
  const out = [];
  out.push('🌿 *Pollen Dots — Hotel Request*');
  out.push(`📋 *Query:* ${qryShortId}`);
  if (h.location) out.push(`📍 *Location:* ${h.location}`);
  if (h.check_in || h.check_out) {
    const n = h.nights ? ` (${h.nights}N)` : '';
    out.push(`📅 *Stay:* ${h.check_in || '—'} → ${h.check_out || '—'}${n}`);
  } else if (h.season) {
    out.push(`📅 *Stay period:* ${h.season} (share seasonal B2B rates)`);
  } else {
    out.push('📅 *Stay:* Dates not fixed — share best B2B rates for the season / year');
  }
  if (rooms) out.push(`🛏 *Rooms:* ${rooms}`);
  if (adults || children) {
    const pax = [];
    if (adults) pax.push(`${adults} Adult${adults === 1 ? '' : 's'}`);
    if (children) pax.push(`${children} Child${children === 1 ? '' : 'ren'}`);
    out.push(`👥 *Pax:* ${pax.join(', ')}`);
  }
  if ((accTypes || []).length) out.push(`🏨 *Acc type:* ${accTypes.join(' / ')}`);
  if (h.room_category) out.push(`🛎 *Category:* ${h.room_category}`);
  if (mealPlan) out.push(`🍽 *Meal plan:* ${mealPlan}`);
  if (h.special_request) {
    out.push('');
    out.push(`⚠️ *Special:* ${h.special_request}`);
  }
  if (guest?.meal_preference && !mealPlan) out.push(`🥗 *Meal pref:* ${guest.meal_preference}`);
  out.push('');
  out.push('🙏 Share your best B2B rate, inclusions and cancellation policy. Need quote in 24h.');
  return out.join('\n');
}

function draftVehicleWhatsApp(qryShortId, v, tour, guest) {
  const out = [];
  out.push('🌿 *Pollen Dots — Transport Request*');
  out.push(`📋 *Query:* ${qryShortId}`);
  if (v.transfer_from || v.transfer_to) {
    out.push(`🛣 *Route:* ${v.transfer_from || '—'} → ${v.transfer_to || '—'}`);
  }
  if (v.date_from || v.date_to) {
    out.push(`📅 *Dates:* ${v.date_from || tour?.start_date || '—'} → ${v.date_to || tour?.end_date || '—'}`);
  }
  if (v.category) out.push(`🚐 *Vehicle:* ${v.category}${v.qty ? ` × ${v.qty}` : ''}`);
  const pax = (parseInt(tour?.adults || 0, 10) || 0) + (parseInt(tour?.children || 0, 10) || 0);
  if (pax) out.push(`👥 *Pax:* ${pax}`);
  if (v.brief) {
    out.push('');
    out.push(`📝 *Brief:* ${v.brief}`);
  }
  if (v.special) {
    out.push('');
    out.push(`⚠️ *Special:* ${v.special}`);
  }
  if (guest?.meal_preference) out.push(`🥗 *Meal pref:* ${guest.meal_preference}`);
  out.push('');
  out.push('🙏 Share your best B2B rate, inclusions (driver allowance, tolls, parking, fuel), cancellation policy. Need quote in 24h.');
  return out.join('\n');
}

function draftGuideWhatsApp(qryShortId, g, tour) {
  const out = [];
  out.push('🌿 *Pollen Dots — Guide Request*');
  out.push(`📋 *Query:* ${qryShortId}`);
  if (g.location) out.push(`📍 *Location:* ${g.location}`);
  if (g.date_from || g.date_to) {
    out.push(`📅 *Dates:* ${g.date_from || '—'} → ${g.date_to || '—'}`);
  }
  const pax = g.guests || ((tour?.adults || 0) + (tour?.children || 0));
  if (pax) out.push(`👥 *Pax:* ${pax}`);
  if ((g.languages || []).length) out.push(`🗣 *Languages:* ${g.languages.join(', ')}`);
  if (g.brief) {
    out.push('');
    out.push(`📝 *Brief:* ${g.brief}`);
  }
  if (g.special) {
    out.push('');
    out.push(`⚠️ *Special:* ${g.special}`);
  }
  out.push('');
  out.push('🙏 Share your daily rate, inclusions and availability. Need quote in 24h.');
  return out.join('\n');
}



// Inline component for each attached-vendor row inside Outreach section
function AttachedVendorRow({ attached, queryId, onChange, onFollowUp }) {
  const [busy, setBusy] = useState(false);
  const updateStatus = async (status) => {
    setBusy(true);
    const t = localStorage.getItem('session_token');
    try {
      const res = await fetch(`${API}/pollen/queries/${queryId}/qualify-vendor`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({ vendor_id: attached.vendor_id, status }),
      });
      if (!res.ok) throw new Error('Failed');
      onChange({ status });
      toast.success(`Marked ${status}`);
    } catch { toast.error('Could not update status'); }
    finally { setBusy(false); }
  };
  const tint =
    attached.status === 'Qualified' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' :
    attached.status === 'Rejected'  ? 'bg-red-50 border-red-200 text-red-900' :
                                       'bg-muted/40 border-border text-foreground';
  return (
    <div className={`rounded border ${tint}`} data-testid={`attached-vendor-${attached.vendor_id}`}>
      <div className="flex items-center gap-2 p-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{attached.vendor_name || attached.vendor_id}</div>
          <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2">
            <span>{attached.vendor_type || ''}</span>
            <span>· {attached.status || 'Contacted'}</span>
            {attached.last_contacted_at && <span>· last {new Date(attached.last_contacted_at).toLocaleDateString()}</span>}
          </div>
        </div>
        <Button size="sm" variant="ghost" disabled={busy || attached.status === 'Qualified'} className="h-7 text-xs text-emerald-700" onClick={() => updateStatus('Qualified')} data-testid={`qualify-${attached.vendor_id}`}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Qualify
        </Button>
        <Button size="sm" variant="ghost" disabled={busy || attached.status === 'Rejected'} className="h-7 text-xs text-red-700" onClick={() => updateStatus('Rejected')} data-testid={`reject-${attached.vendor_id}`}>
          <XCircle className="w-3.5 h-3.5" />
        </Button>
      </div>
      {onFollowUp && (
        <div className="px-2 pb-2">
          <Button size="sm" variant="outline"
            className="w-full h-8 text-[11px] border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
            data-testid={`followup-${attached.vendor_id}`}
            onClick={() => onFollowUp(attached)}>
            <Mail className="w-3 h-3 mr-1" />
            Follow up (single recipient — no BCC chain)
          </Button>
        </div>
      )}
    </div>
  );
}

// ---- Per-section outreach body composers -----------------------------------

// Vehicle leg label used in subject stamp. Falls back gracefully when the
// operator hasn't filled transfer/from-to.
function vehicleLegLabel(v) {
  if (v.transfer_from && v.transfer_to) return `${v.transfer_from} → ${v.transfer_to}`;
  if (v.category && v.qty) return `${v.qty}× ${v.category}`;
  return v.category || 'Transport';
}

// Draft a vehicle/leg-specific outreach body.
function draftVehicleBody(qryShortId, v, tour, guest) {
  const lines = [];
  lines.push('Hello,');
  lines.push('');
  lines.push('Greetings from Pollen Dots.');
  lines.push('');
  lines.push(`We need transport for query ${qryShortId} with the following details:`);
  lines.push('');
  if (v.transfer_from || v.transfer_to) {
    lines.push(`• Route: ${v.transfer_from || '—'} → ${v.transfer_to || '—'}`);
  }
  if (v.date_from || v.date_to) {
    lines.push(`• Dates: ${v.date_from || tour?.start_date || '—'} → ${v.date_to || tour?.end_date || '—'}`);
  }
  if (v.category) lines.push(`• Vehicle type: ${v.category}${v.qty ? ` × ${v.qty}` : ''}`);
  const pax = (parseInt(tour?.adults || 0, 10) || 0) + (parseInt(tour?.children || 0, 10) || 0);
  if (pax) lines.push(`• Pax: ${pax}`);
  if (v.brief) {
    lines.push('');
    lines.push(`Brief: ${v.brief}`);
  }
  if (v.special) {
    lines.push('');
    lines.push(`Special request: ${v.special}`);
  }
  if (guest?.meal_preference) {
    lines.push(`• Guest meal preference: ${guest.meal_preference}`);
  }
  lines.push('');
  lines.push('Please share your best B2B rate, inclusions (driver allowance, tolls, parking, fuel) and cancellation policy. We need a quote within 24 hours.');
  lines.push('');
  lines.push('Looking forward.');
  return lines.join('\n');
}

// Draft a guide-specific outreach body.
function draftGuideBody(qryShortId, g, tour) {
  const lines = [];
  lines.push('Hello,');
  lines.push('');
  lines.push('Greetings from Pollen Dots.');
  lines.push('');
  lines.push(`We need a guide for query ${qryShortId} with the following details:`);
  lines.push('');
  if (g.location) lines.push(`• Location: ${g.location}`);
  if (g.date_from || g.date_to) {
    lines.push(`• Dates: ${g.date_from || '—'} → ${g.date_to || '—'}`);
  }
  if (g.guests) lines.push(`• Pax: ${g.guests}`);
  else if (tour?.adults || tour?.children) {
    lines.push(`• Pax: ${(tour.adults || 0) + (tour.children || 0)}`);
  }
  if ((g.languages || []).length) lines.push(`• Language(s): ${g.languages.join(', ')}`);
  if (g.brief) {
    lines.push('');
    lines.push(`Brief itinerary (guide portion): ${g.brief}`);
  }
  if (g.special) {
    lines.push('');
    lines.push(`Special request: ${g.special}`);
  }
  lines.push('');
  lines.push('Please share your daily rate, inclusions, and availability. We need a quote within 24 hours.');
  lines.push('');
  lines.push('Looking forward.');
  return lines.join('\n');
}

// Consolidated package body — assembles Tour + Hotels + Vehicles + Guides
// into a single readable brief so a packaged operator can quote the full
// trip in one go. Honors `packageIncludes` ticks: omits sections the
// operator didn't tick (e.g. don't list vehicles if only Hotels were ticked).

// ----- Trip-flow generator ---------------------------------------------------
// Day-by-day overlay derived from the OVERNIGHT HALTS (not from picked
// vehicle legs — those are just a requirement chip). Logic:
//   • From→To = prev_overnight → today_overnight, with arrival on day 1 and
//     departure on the last day. Same overnight on consecutive days = stay.
//   • Hotel requirement: overnight halt exists today.
//   • Vehicle requirement: it's a travel day (arrival / overnight changed
//     / departure).
//   • Guide requirement: a guide assignment covers today.
// Returns '' when there isn't enough data (no start_date).
function buildTripFlow(tour, hotels, vehicles, guides) {
  const start = tour?.start_date;
  if (!start) return '';
  const startDt = new Date(`${start}T00:00:00`);
  if (isNaN(startDt.getTime())) return '';

  // Trip length — prefer tour.nights, else derive from end_date, else
  // fallback to the max check-out date among halts.
  let nights = parseInt(tour?.nights || '0', 10) || 0;
  if (!nights && tour?.end_date) {
    const end = new Date(`${tour.end_date}T00:00:00`);
    if (!isNaN(end.getTime())) nights = Math.max(0, Math.round((end - startDt) / 86400000));
  }
  if (!nights) {
    for (const h of (hotels || [])) {
      if (h.check_out) {
        const co = new Date(`${h.check_out}T00:00:00`);
        if (!isNaN(co.getTime())) {
          const diff = Math.round((co - startDt) / 86400000);
          if (diff > nights) nights = diff;
        }
      }
    }
  }
  const days = Math.max(1, nights + 1);
  if (days > 60) return '';

  const fmtDate = (dt) => {
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(dt.getDate()).padStart(2, '0')} ${m[dt.getMonth()]}`;
  };
  const inRange = (date, from, to) => {
    if (!from && !to) return false;
    const f = from ? new Date(`${from}T00:00:00`) : null;
    const t = to ? new Date(`${to}T00:00:00`) : null;
    return (!f || date >= f) && (!t || date <= t);
  };
  const overnightOnDay = (dayIdx) => {
    const cur = new Date(startDt.getTime() + dayIdx * 86400000);
    return (hotels || []).find(h => {
      if (!h.check_in) return false;
      const ci = new Date(`${h.check_in}T00:00:00`);
      const co = h.check_out ? new Date(`${h.check_out}T00:00:00`) : null;
      return cur >= ci && (!co || cur < co);
    });
  };

  // Two passes — first compute every row's strings, then dynamically pad
  // the From→To and Overnight columns so the table aligns regardless of
  // the longest destination name.
  const rows = [];
  let maxFromTo = 'From → To'.length;
  let maxOvernight = 'Overnight'.length;
  for (let d = 0; d < days; d++) {
    const cur = new Date(startDt.getTime() + d * 86400000);
    const halt = overnightOnDay(d);
    const prev = d > 0 ? overnightOnDay(d - 1) : null;
    const grd = (guides || []).find(g => g.location && inRange(cur, g.date_from, g.date_to));
    const todayLoc = halt?.location || '';
    const prevLoc = prev?.location || '';

    let fromTo, isMovement;
    if (d === 0) {
      fromTo = todayLoc ? `Arrive ${todayLoc}` : 'Arrive';
      isMovement = true;
    } else if (d === days - 1 && !halt) {
      fromTo = prevLoc ? `${prevLoc} → Departure` : 'Departure';
      isMovement = true;
    } else if (todayLoc && prevLoc && todayLoc !== prevLoc) {
      fromTo = `${prevLoc} → ${todayLoc}`;
      isMovement = true;
    } else if (todayLoc) {
      fromTo = todayLoc;          // stay — same overnight as yesterday
      isMovement = false;
    } else {
      fromTo = '—';
      isMovement = false;
    }
    const overnight = halt ? (halt.location || '—') : (d === days - 1 ? 'Departure' : '—');
    const reqs = [];
    if (halt) reqs.push('Hotel');
    if (isMovement) reqs.push('Vehicle');
    if (grd) reqs.push('Guide');
    const reqStr = reqs.length ? reqs.join(' + ') : '—';

    rows.push({ d: d + 1, date: fmtDate(cur), fromTo, overnight, reqStr });
    if (fromTo.length > maxFromTo) maxFromTo = fromTo.length;
    if (overnight.length > maxOvernight) maxOvernight = overnight.length;
  }

  const pad = (s, w) => (s + ' '.repeat(w)).slice(0, w);
  const out = [];
  out.push(`Day | Date    | ${pad('From → To', maxFromTo)} | ${pad('Overnight', maxOvernight)} | Requirements`);
  for (const r of rows) {
    out.push(`${String(r.d).padStart(3, ' ')} | ${r.date} | ${pad(r.fromTo, maxFromTo)} | ${pad(r.overnight, maxOvernight)} | ${r.reqStr}`);
  }
  return out.join('\n');
}

// WhatsApp/Telegram-friendly compact brief — same intel as the email body
// but flattened with emoji markers so it reads cleanly on a phone chat
// without bullet indents getting mangled.
function draftPackageWhatsApp(qryShortId, { tour, guest, hotels, vehicles, guides, pkg, packageIncludes }) {
  const includes = (packageIncludes && packageIncludes.length)
    ? packageIncludes
    : ['hotel', 'transport', 'guide'];
  const tourAcc = (tour?.accommodation_types || []);
  const haltAccSet = new Set();
  for (const h of (hotels || [])) {
    if (h.override) (h.overrides?.accommodation_types || []).forEach(x => haltAccSet.add(x));
  }
  const effectiveAcc = tourAcc.length ? tourAcc : [...haltAccSet];
  const effectiveMeal = (tour?.meal_plan || '') || ((hotels || []).find(h => h.override && h.overrides?.meal_plan)?.overrides?.meal_plan || '');

  const adults = tour?.adults || 0;
  const children = tour?.children || 0;
  const pax = [adults && `${adults} Adult${adults === 1 ? '' : 's'}`, children && `${children} Child${children === 1 ? '' : 'ren'}`]
    .filter(Boolean).join(', ');
  const fmtShort = (d) => {
    if (!d) return '';
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt.getTime())) return d;
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(dt.getDate()).padStart(2, '0')} ${m[dt.getMonth()]}`;
  };
  const dateRange = (tour?.start_date || tour?.end_date)
    ? `${fmtShort(tour.start_date) || '—'} → ${fmtShort(tour.end_date) || '—'}${tour?.nights ? ` (${tour.nights}N)` : ''}`
    : '';

  const out = [];
  out.push('🌿 *Pollen Dots — Package Quote*');
  out.push(`📋 *Query:* ${qryShortId}`);
  if (pax) out.push(`👥 *Pax:* ${pax}`);
  if (dateRange) out.push(`📅 *Dates:* ${dateRange}`);
  if (tour?.rooms) out.push(`🛏 *Rooms:* ${tour.rooms}`);
  if (effectiveAcc.length) out.push(`🏨 *Acc:* ${effectiveAcc.join(' / ')}`);
  if (effectiveMeal) out.push(`🍽 *Meal:* ${effectiveMeal}`);

  if (pkg?.brief) {
    out.push('');
    out.push('📜 *Itinerary*');
    out.push(pkg.brief);
  }

  // Trip flow — same overlay as the email body. Already pre-formatted as a
  // compact table; we just print it verbatim so WhatsApp readers see the
  // exact same day/route/overnight/requirements grid.
  const tf = buildTripFlow(tour, hotels || [], vehicles || [], guides || []);
  if (tf) {
    out.push('');
    out.push('🗺 *Trip flow*');
    out.push('```');
    out.push(tf);
    out.push('```');
  }

  out.push('');
  out.push(`📦 *Scope:* ${includes.map(i => i[0].toUpperCase() + i.slice(1)).join(' + ')}`);

  if (pkg?.special) {
    out.push('');
    out.push(`⚠️ *Special:* ${pkg.special}`);
  }
  if (guest?.meal_preference && !effectiveMeal) {
    out.push(`🥗 *Meal pref:* ${guest.meal_preference}`);
  }

  out.push('');
  out.push('🙏 Please share consolidated B2B rate with line-item breakup, inclusions/exclusions, and cancellation policy. Need quote in 48 hours.');
  return out.join('\n');
}



// ----- Pretty date + per-day route helpers ----------------------------------
// "21st June 26" style dates expected by operators in vendor emails.
function _ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
function _fmtPrettyDate(iso) {
  if (!iso) return '';
  const dt = new Date(`${iso}T00:00:00`);
  if (isNaN(dt.getTime())) return iso;
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return `${_ordinal(dt.getDate())} ${months[dt.getMonth()]} ${String(dt.getFullYear()).slice(-2)}`;
}
// Compact a free-form day description into a dash-separated route. Handles
// the common Indian itinerary phrasings:
//   "Drive from X to Y."        → "X - Y"
//   "X to Y via Z."             → "X - Z - Y"
//   "X to Y and Golden Temple." → "X - Y"  (strip trailing "and …")
//   "X, Y, and Z."              → "X - Y - Z"
//   "X local exploration …"     → "X"
// Falls back to the trimmed original when no pattern matches.
function _compactDayRoute(text, prevLoc) {
  if (!text) return '';
  let s = String(text).trim().replace(/\.+$/, '');
  s = s.replace(/^(Drive|Travel|Transfer|Proceed|Move|Journey|Continue|Depart)\s+(from\s+)?/i, '');
  let m = s.match(/^(.+?)\s+to\s+(.+?)\s+via\s+(.+)$/i);
  if (m) return `${m[1].trim()} - ${m[3].trim()} - ${m[2].trim()}`;
  m = s.match(/^to\s+(.+?)\s+via\s+(.+)$/i);
  if (m) {
    const dest = m[1].trim(); const via = m[2].trim();
    return prevLoc ? `${prevLoc} - ${via} - ${dest}` : `${via} - ${dest}`;
  }
  m = s.match(/^(.+?)\s+to\s+(.+?)\s+and\s+.+$/i);
  if (m) return `${m[1].trim()} - ${m[2].trim()}`;
  m = s.match(/^(.+?)\s+to\s+(.+)$/i);
  if (m) return `${m[1].trim()} - ${m[2].trim()}`;
  // "to X" with no source — prepend previous overnight when known.
  m = s.match(/^to\s+(.+)$/i);
  if (m) {
    const dest = m[1].trim();
    return prevLoc ? `${prevLoc} - ${dest}` : dest;
  }
  m = s.match(/^(.+?),\s*(.+?),?\s*and\s+(.+)$/i);
  if (m) return `${m[1].trim()} - ${m[2].trim()} - ${m[3].trim()}`;
  m = s.match(/^([A-Z][\w\s]+?)\s+(local|exploration|sightseeing|stay|leisure|departure|monasteries|day)/i);
  if (m) return m[1].trim();
  return s;
}
// Produce the tight per-day itinerary lines:
//   "Day N | <pretty date> | <route>"
// Walks the LLM-parsed `pkg.brief` (map of Day N → text) when available,
// else falls back to halt-derived From-To labels.
function buildDaywiseItinerary(tour, hotels, pkgBrief) {
  const start = tour?.start_date;
  if (!start) return '';
  const startDt = new Date(`${start}T00:00:00`);
  if (isNaN(startDt.getTime())) return '';
  let nights = parseInt(tour?.nights || '0', 10) || 0;
  if (!nights && tour?.end_date) {
    const end = new Date(`${tour.end_date}T00:00:00`);
    if (!isNaN(end.getTime())) nights = Math.max(0, Math.round((end - startDt) / 86400000));
  }
  const days = Math.max(1, nights + 1);
  if (days > 60) return '';

  const briefMap = _parseBriefByDay(pkgBrief || '');
  const overnightOnDay = (idx) => {
    const cur = new Date(startDt.getTime() + idx * 86400000);
    return (hotels || []).find(h => {
      if (!h.check_in) return false;
      const ci = new Date(`${h.check_in}T00:00:00`);
      const co = h.check_out ? new Date(`${h.check_out}T00:00:00`) : null;
      return cur >= ci && (!co || cur < co);
    });
  };

  const lines = [];
  // Track the "last seen" location so brief lines without an explicit
  // source ("Drive to X") can be enriched with the previous day's
  // overnight or destination.
  let lastLoc = '';
  for (let d = 0; d < days; d++) {
    const dn = d + 1;
    const cur = new Date(startDt.getTime() + d * 86400000);
    const date = _fmtPrettyDate(cur.toISOString().slice(0, 10));
    const halt = overnightOnDay(d);
    const prev = d > 0 ? overnightOnDay(d - 1) : null;
    const cloc = halt?.location || '';
    const ploc = prev?.location || lastLoc;
    let route = '';
    if (briefMap[dn]) {
      route = _compactDayRoute(briefMap[dn], ploc);
    } else {
      if (d === 0) route = cloc ? `Arrive ${cloc}` : 'Arrive';
      else if (d === days - 1 && !halt) route = ploc ? `${ploc} - Departure` : 'Departure';
      else if (cloc && ploc && cloc !== ploc) route = `${ploc} - ${cloc}`;
      else route = cloc || '—';
    }
    // Remember the trailing destination of this day so the next day's
    // "to X" line can resolve cleanly.
    const tail = route.split(' - ').slice(-1)[0];
    if (tail && tail !== '—' && !/Departure/i.test(tail)) lastLoc = tail;
    lines.push(`Day ${dn} | ${date} | ${route}`);
  }
  return lines.join('\n');
}

function draftPackageBody(qryShortId, { tour, guest, hotels, vehicles, guides, pkg, packageIncludes }) {
  const includes = (packageIncludes && packageIncludes.length)
    ? packageIncludes
    : ['hotel', 'transport', 'guide']; // default: full package
  const wantsHotels = includes.includes('hotel');
  const wantsTransport = includes.includes('transport');
  const wantsGuides = includes.includes('guide');

  // Resolve accommodation_types / meal_plan with halt-level fallback.
  const tourAcc = (tour?.accommodation_types || []);
  const tourMeal = (tour?.meal_plan || '');
  const haltAccSet = new Set();
  let firstHaltMeal = '';
  for (const h of (hotels || [])) {
    if (h.override) {
      const ov = h.overrides || {};
      (ov.accommodation_types || []).forEach(x => haltAccSet.add(x));
      if (!firstHaltMeal && ov.meal_plan) firstHaltMeal = ov.meal_plan;
    }
  }
  const effectiveAcc = tourAcc.length ? tourAcc : [...haltAccSet];
  const effectiveMeal = tourMeal || firstHaltMeal;

  const lines = [];
  lines.push('Hello,');
  lines.push('');
  lines.push('Greetings from Pollen Dots.');
  lines.push('');
  lines.push(`We are seeking a packaged quote for query ${qryShortId}.`);

  // --- Itinerary: one row per day, dash-route format -----------------------
  const itinerary = buildDaywiseItinerary(tour, hotels || [], pkg?.brief || '');
  if (itinerary) {
    lines.push('');
    lines.push('Itinerary:');
    lines.push(itinerary);
  }

  // --- Hotel Requirements (only if Hotel is in scope) ----------------------
  if (wantsHotels) {
    const checkIns = (hotels || []).map(h => h.check_in).filter(Boolean).sort();
    const checkOuts = (hotels || []).map(h => h.check_out).filter(Boolean).sort();
    const hFrom = checkIns[0] || tour?.start_date || '';
    const hTo = checkOuts.length ? checkOuts[checkOuts.length - 1] : tour?.end_date || '';
    if (hFrom || hTo) {
      lines.push('');
      lines.push('Hotel Requirements:');
      if (hFrom || hTo) lines.push(`From ${_fmtPrettyDate(hFrom) || '—'} to ${_fmtPrettyDate(hTo) || '—'}`);
      if (tour?.rooms) {
        const adults = parseInt(tour?.adults || 0, 10) || 0;
        const sharing = adults <= 1 ? 'single sharing'
                     : adults === 2 ? 'double sharing'
                     : adults === 3 ? 'triple sharing'
                     : adults === 4 ? 'quad sharing'
                     : `${adults}-pax sharing`;
        lines.push(`${tour.rooms} Room${tour.rooms > 1 ? 's' : ''} on ${sharing}`);
      }
      if (effectiveMeal) lines.push(`${effectiveMeal} Plan`);
      if (effectiveAcc.length) lines.push(`Accommodation Type: ${effectiveAcc.join(' / ')}`);
    }
  }

  // --- Vehicle Requirements (only if Transport is in scope) ---------------
  if (wantsTransport && (vehicles || []).length) {
    const vFrom = (vehicles.map(v => v.date_from).filter(Boolean).sort()[0]) || tour?.start_date || '';
    const vTo = (vehicles.map(v => v.date_to).filter(Boolean).sort().slice(-1)[0]) || tour?.end_date || '';
    const cats = [...new Set(vehicles.map(v => v.category).filter(Boolean))];
    lines.push('');
    lines.push('Vehicle Requirements:');
    if (vFrom || vTo) lines.push(`From ${_fmtPrettyDate(vFrom) || '—'} to ${_fmtPrettyDate(vTo) || '—'}`);
    if (cats.length) lines.push(`${cats.join(' / ')}`);
    const pax = (parseInt(tour?.adults || 0, 10) || 0) + (parseInt(tour?.children || 0, 10) || 0);
    if (pax) lines.push(`${pax} pax`);
  }

  // --- Guide Requirements (only if Guide is in scope) ---------------------
  if (wantsGuides && (guides || []).length) {
    const gFroms = guides.map(g => g.date_from).filter(Boolean).sort();
    const gTos = guides.map(g => g.date_to).filter(Boolean).sort();
    const gFrom = gFroms[0] || '';
    const gTo = gTos.length ? gTos[gTos.length - 1] : '';
    const langSet = new Set();
    guides.forEach(g => (g.languages || []).forEach(l => langSet.add(l)));
    const langs = [...langSet];
    lines.push('');
    lines.push('Guide Requirements:');
    if (gFrom || gTo) lines.push(`From ${_fmtPrettyDate(gFrom) || '—'} to ${_fmtPrettyDate(gTo) || '—'}`);
    if (langs.length) lines.push(`${langs.join(' & ')} Speaking`);
  }

  lines.push('');
  lines.push(`Package scope: ${includes.map(i => i[0].toUpperCase() + i.slice(1)).join(' + ')}`);

  if (pkg?.special) {
    lines.push('');
    lines.push(`Special request: ${pkg.special}`);
  }

  lines.push('');
  lines.push('Please share a consolidated B2B rate with line-item breakup (hotels, transport, guide as applicable), inclusions/exclusions, and cancellation policy.');
  lines.push('');
  lines.push('Looking forward.');
  return lines.join('\n');
}



export default QueryWorkspace;
